"use client"

import type React from "react"
import { useState, useEffect, useMemo, useRef } from "react"
import Link from "next/link"
import { AlertTriangle, ArrowRight, Bug, Leaf, ShieldAlert, Droplets, Sun, ChevronDown, ChevronUp, Camera, CheckCircle, Shield, Search, Lock, Package, Wrench, CalendarDays, Clock, Sprout } from "lucide-react"
import type { User } from "@supabase/supabase-js"

import { cn } from "@/lib/utils"
import {
  analysisFunnelEnvelope,
  analytics,
  stashEmailCaptureAttribution,
  trackEvent,
  type EventProperties,
  type ResultStateId,
} from "@/lib/analytics"
import { weedPageDisplayName } from "@/data/weeds/slugs"
import { pestPageDisplayName, pestSlugForKey } from "@/data/pests/slugs"
import { getDiagnosisNextStep, type DiagnosisNextStep } from "@/lib/diagnosis-condition-routing"
import {
  REFINEMENT_GUIDANCE_FALLBACK,
  type RefinementPolicy,
  type RefinementReason,
  type RefinementTrigger,
} from "@/lib/analysis/refinement-policy"
import { MAX_PINS, projectPinThroughCover } from "@/lib/diagnosis-pins"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { AnalysisResultFrame } from "@/components/analysis/analysis-result-primitives"
import { InconclusivePlanAsk } from "@/components/analysis/inconclusive-plan-ask"
import { CollapsibleSection } from "@/components/tools/shared/collapsible-section"
import { SocialLoginButtons } from "@/components/auth/SocialLoginButtons"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"
import { useInViewportImpression, useVisibleImpression } from "@/hooks/use-visible-impression"
import { useOfferedActionsSnapshot } from "@/hooks/use-offered-actions-snapshot"
import { ConfidenceMeter } from "./confidence-meter"
import { CarePlanSentSuccess } from "./care-plan-sent-success"
import { ResultConfidenceFeedback } from "./result-confidence-feedback"
import { DiagnosisReviewRequest } from "./diagnosis-review-request"
import { ResultToolDeeplinks } from "./result-tool-deeplinks"
import { ResultUpgradeCard } from "./result-upgrade-card"
import { ResultStickySaveBar } from "./result-sticky-save-bar"
import { SavedPlanActionCard } from "./saved-plan-action-card"
import { PhotoTips } from "./photo-tips"
import { DiagnosisProductPicks, HealthyDiagnosisProductPicks } from "./diagnosis-product-picks"
import { DiagnosisProReferralCard } from "./diagnosis-pro-referral-card"
import { ResultEmailCaptureCard } from "@/components/tools/shared/result-email-capture-card"
import { AppDownloadCTA, AppDownloadTextLink } from "@/components/ui/app-download-cta"
import { TrackedAffiliateLink } from "@/components/ui/tracked-affiliate-link"
import { getRegionFromZipCode, REGION_DEFAULT_GRASS } from "@/data/regional-climate"
import { getAllSeasonalTips } from "@/lib/seasonal-tips"
import { getMonthName, getCurrentMonthIndex } from "@/data/guide-monthly-tasks"
import { findAffiliateEntry } from "@/data/affiliate-product-catalog"
import { getCompleteConditionPicks, getConditionTreatmentFallback } from "@/data/condition-product-picks"
import type { EmailSuggestion } from "@/lib/email-typo-detection"
import { getCurrentSeason } from "@/lib/date-utils"
import { isHighConfidence as isHighConfidenceScore } from "@/lib/confidence-display"
import { savedPlanEventProperties, type SavedPlanAnalyticsContext } from "@/lib/saved-plan-analytics"
import type { DiagnosisResult, DiagnosisIdentification, TreatmentRecommendation } from "@/lib/types/diagnosis"


interface DiagnosisResultsProps {
  result: DiagnosisResult
  uploadedImage: string
  zipCode?: string
  savedAnalysisId?: string | null
  funnelId?: string | null
  attemptId?: string | null
  /** Suppresses the paid continuation for existing subscribers. Identify reads
      the same fact off scanUsage; diagnose has no usage envelope in its props. */
  isPremium?: boolean
  /** True while entitlement is still unknown; the paid continuation stays
      hidden until it settles so a subscriber is never sold an upsell. */
  subscriptionLoading?: boolean
  originLawnId?: string | null
  selectedLawnId?: string | null
  zipMatch?: boolean | null
  onReset: (event?: React.MouseEvent<HTMLElement>) => void
  /** Inconclusive-only accuracy recovery: keeps the primary photo + ZIP and
   *  asks for one targeted supporting view (quota-exempt within the rescan
   *  window). Falls back to onReset when absent. Same contract as
   *  ResultsDisplay.onRefineWithSupportingPhoto. */
  onRefineWithSupportingPhoto?: (reason?: RefinementTrigger) => void
  /** Anonymous visitor: the CTA still renders, and the tap opens the sign-in
   *  gate rather than the photo step. */
  refinementLocked?: boolean
  /** True while the host has a refinement gate or the supporting-photo step
   *  open. The result stays mounted behind both, so the offer must not accept
   *  a second tap and start the same refinement twice. */
  refinementPending?: boolean
  refinementPolicy?: RefinementPolicy | null
  /** Fires once per result when a refinement offer renders, locked or not.
   *  Carries the trigger so the reason CTA and the default additive retry
   *  stay separable in the funnel. */
  onRefinementGateShown?: (trigger: RefinementTrigger) => void
  onSwitchToIdentify: (event?: React.MouseEvent<HTMLElement>) => void
  switchToIdentifyHref?: string
  user: User | null
  authLoading?: boolean
  redirectUrl?: string
  onBeforeOAuth?: () => void

  // Email-capture state + handlers (mirrors ResultsDisplay). Threaded from the
  // host page's useHomepageAnalysis. Optional so callers that don't yet wire the
  // full email flow (the live /diagnose page) keep compiling — the email card
  // only renders when the handlers are present.
  carePlanEmail?: string
  carePlanEmailError?: string | null
  emailSuggestion?: EmailSuggestion | null
  isSubmittingCarePlan?: boolean
  emailGateCompleted?: boolean
  timelineRedirectUrl?: string
  onEmailChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onEmailKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>, location?: string) => void
  /** `location` is sent on care_plan_cta_clicked. */
  onEmailSubmit?: (location?: string) => void
  onPrimaryCtaClick?: (method: "oauth" | "email") => void
  onAcceptEmailSuggestion?: () => void
  onDismissEmailSuggestion?: () => void
  onSeeCarePlan?: () => void

  // Authenticated next-steps (ResultQuickLinks). Optional for the same reason.
  onSaveAndViewTimeline?: () => void
  onViewTimeline?: () => void

  /** Funnel surface for sticky-bar analytics. "diagnose" (live flow, default)
   *  or "saved_result" (the /result/[id] page). NOT derivable from
   *  savedAnalysisId — that's set for fresh anonymous diagnoses too. */
  entryPath?: string

  className?: string
}

// Diagnosis has no grass type, so the deeplinks/quicklinks pass an empty grass
// string — the URL builders omit the `grass` param when it's empty, and the
// tool grid still pre-fills from the ZIP.
const DIAGNOSIS_GRASS_TYPE = ""

// Repeat taps on a refinement CTA inside this window are the same intent.
const REFINEMENT_TAP_GUARD_MS = 1200

// One `result_state_shown` per `${attemptId}:${state}`, module-scoped so a
// remount of the same result (a rerender behind the sign-in gate) cannot
// re-emit the impression that the click rate is measured against.
const resultStateImpressionKeys = new Set<string>()

// Diagnosis confidence is the model's ranking score for this match. It is not
// calibrated against outcomes, and it is a different quantity from the
// species-ID score in lib/confidence-display, so it carries its own wording.
const DIAGNOSIS_SCORE_CAVEAT = "A match score from your photo, not a guarantee."

// Unified card chrome for the above-the-fold hero system. Every elevated
// surface in the verdict (plan card, ProblemCard, the metric panel) shares the
// SAME radius + hairline + shadow so the page reads as one crafted composition
// rather than a stack of mismatched cards. The shadow is a layered, low-opacity
// pair with a faint green cast (matching the brand) — soft depth in the Linear/
// Stripe register, not a hard drop shadow. Centralized here so the system stays
// consistent if the token ever changes.
const CARD_RADIUS = "rounded-2xl"
const CARD_BORDER = "border border-green-100/80"
const CARD_SHADOW =
  "shadow-[0_1px_2px_rgba(16,24,16,0.04),0_8px_24px_-12px_rgba(22,101,52,0.12)]"
// The hero card surfaces (plan card, problem cards) compose the three together.
const PREMIUM_CARD = `${CARD_RADIUS} ${CARD_BORDER} ${CARD_SHADOW}`

function getSeverityStyles(severity: string) {
  switch (severity) {
    case 'severe':
      return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500' }
    case 'moderate':
      return { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500' }
    case 'mild':
    default:
      // Mild reads as healthy/under-control — use the page's green aesthetic
      // rather than a flat gray, so a mild diagnosis still feels on-brand.
      return { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', dot: 'bg-green-500' }
  }
}

function getUrgencyLabel(urgency: string) {
  switch (urgency) {
    case 'immediate': return 'Act Now'
    case 'soon': return 'Treat Soon'
    case 'monitor':
    default: return 'Monitor'
  }
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// One concrete, human-readable step from a treatment — the free "taste" shown in
// the anon lead card so users see the plan has real depth behind the gate. Prefer
// a cultural practice (reads as a clean instruction), then a product action, then
// an organic alternative. Returns null when the model gave us none of these
// (diagnosis data quality varies by condition) so the card degrades gracefully.
function firstTreatmentStep(rec: TreatmentRecommendation | undefined): string | null {
  if (!rec) return null
  const practice = rec.culturalPractices?.find((p) => p?.trim())
  if (practice) return practice.trim()
  const product = rec.products?.find((p) => p?.name?.trim())
  if (product) return `Apply ${product.name.trim()}`
  const organic = rec.organicAlternatives?.find((o) => o?.trim())
  if (organic) return organic.trim()
  return null
}

// Smooth-scroll to the diagnosis plan. Works in both the live /diagnose flow
// and the saved /result view since both render the same #diagnosis-plan anchor.
function scrollToDiagnosisPlan() {
  if (typeof document === 'undefined') return
  document.getElementById('diagnosis-plan')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

// Identity for the compact save strip under the immediate actions: its
// `cta_location` on both the impression and the click, plus the OAuth capture
// attribution, so the strip reads separately from the gate in every funnel.
//
// The strip is a HANDOFF, not a capture surface. Neither lane can ever emit
// `email_captured` under this string: "Email it" sends the user to the gate,
// which submits as `diagnosis_gate`, and "Save with Google" is credited on
// `signup_completed.capture_source` by the auth callback. Its impression
// therefore belongs on `cta_surface_shown` (keyed on cta_location, joining the
// `result_primary_cta_clicked` it already fires) and NOT on
// `email_capture_surface_shown`, whose contract is that the surface's own
// submit reports the same source.
const ACTIONS_STRIP_SOURCE = "diagnosis_actions_strip"

// The save gate's email-capture source. Shared so the card's own events and the
// reveal fired on its behalf (see sendUserToGate) never drift apart — a mismatch
// would split one funnel across two source values.
const GATE_SOURCE = "diagnosis_gate"
// care_plan_cta_clicked `location` for the digest's email lane (button and Enter).
const DIGEST_CARE_PLAN_LOCATION = "diagnosis_result_digest"

// Impressions ride the shared non-email `cta_surface_shown` keyed on this
// cta_location, so the link has a CTR denominator. The click deliberately does
// NOT ride a shared CTA event: a content click and a capture click must stay
// separable, so a read can show whether captures held while content clicks rose.
const CONDITION_LINK_LOCATION = "diagnosis_condition_next_step"

// Matches the two-pulse .cta-beacon animation in app/globals.css.
const GATE_BEACON_MS = 2400
// Long enough for the smooth scroll to settle before the caret lands, so focus
// never fights the scroll (preventScroll is belt-and-suspenders on top).
const GATE_FOCUS_DELAY_MS = 450

// Send the user to the anonymous save gate: scroll to it, replay the beacon so
// the click has a visible landing spot, then drop the caret in its email field.
// Shared by the hero peak CTA and the actions save strip. The beacon is toggled
// via classList + forced reflow (the canonical CSS-animation restart trick) so a
// second click replays it — the same approach as the upload card's beacon;
// React batching makes a state toggle unreliable here. The gate card is reached
// by its `#care-plan-upgrade` anchor (already the scroll target), but the email
// field is reached by ref — it lives inside ResultUpgradeCard.
//
// The gate's email lane sits behind a "get it by email" reveal link, so the ref
// is null until it opens. revealEmail runs first and the focus is already on a
// timer, which gives React room to mount the input before the caret arrives.
function sendUserToGate(
  emailInputRef: React.RefObject<HTMLInputElement | null>,
  revealEmail?: () => void,
) {
  if (typeof document === "undefined") return
  revealEmail?.()
  const gate = document.getElementById("care-plan-upgrade")
  gate?.scrollIntoView({ behavior: "smooth", block: "start" })
  if (gate) {
    gate.classList.remove("cta-beacon")
    // Forced reflow — read offsetWidth so the class removal flushes before the
    // re-add restarts the animation.
    void gate.offsetWidth
    gate.classList.add("cta-beacon")
    window.setTimeout(() => gate.classList.remove("cta-beacon"), GATE_BEACON_MS)
  }
  window.setTimeout(
    () => emailInputRef.current?.focus({ preventScroll: true }),
    GATE_FOCUS_DELAY_MS,
  )
}

function getTypeIcon(type: DiagnosisIdentification['type']) {
  switch (type) {
    case 'disease': return <ShieldAlert className="w-4 h-4" />
    case 'pest': return <Bug className="w-4 h-4" />
    case 'weed': return <Leaf className="w-4 h-4" />
    case 'nutrient_deficiency': return <Droplets className="w-4 h-4" />
    case 'environmental': return <Sun className="w-4 h-4" />
  }
}

function getTypeLabel(type: DiagnosisIdentification['type']) {
  switch (type) {
    case 'disease': return 'Disease'
    case 'pest': return 'Pest'
    case 'weed': return 'Weed'
    case 'nutrient_deficiency': return 'Nutrient Deficiency'
    case 'environmental': return 'Environmental Stress'
  }
}

// Deep link to the live guide or calculator for a normalized condition. Own
// component so the impression hook is only mounted when the link actually
// renders (the wrapper pattern useImpressionEvent requires); the caller keeps
// the null gate.
function ConditionNextStepLink({
  nextStep,
  problemType,
}: {
  nextStep: DiagnosisNextStep
  problemType: DiagnosisIdentification['type']
}) {
  const impressionProps: EventProperties = {
    condition_id: nextStep.conditionId,
    destination: nextStep.href,
    problem_type: problemType,
  }
  const impressionRef = useImpressionEvent<HTMLParagraphElement>(
    CONDITION_LINK_LOCATION,
    impressionProps,
    { emit: analytics.ctaSurfaceShownWhenVisible },
  )

  return (
    <p ref={impressionRef}>
      <Link
        href={nextStep.href}
        onClick={() =>
          trackEvent('diagnosis_condition_link_clicked', {
            cta_location: CONDITION_LINK_LOCATION,
            cta_text: nextStep.label,
            ...impressionProps,
          })
        }
        className="inline-flex min-h-[36px] items-center text-sm font-medium text-green-700 underline underline-offset-2 hover:text-green-800"
      >
        {nextStep.label}
      </Link>
    </p>
  )
}

// Immediate/recommended actions — one renderer, two urgency tones. `severe`
// shows a prominent red numbered checklist (safety-first, sits at the top of the
// result); otherwise a calmer amber "recommended actions" list lower down. The
// two are mutually exclusive (the caller gates on hasSevereProblems), so this
// centralizes what used to be two near-duplicate inline blocks.
function ImmediateActionsBlock({ actions, severe }: { actions: string[]; severe: boolean }) {
  if (severe) {
    return (
      <div className={cn("max-w-3xl border border-red-200 bg-white overflow-hidden", CARD_RADIUS, CARD_SHADOW)}>
        <div className="flex items-center gap-3 border-b border-red-100 bg-red-50 px-4 py-3 md:px-5">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-red-100 text-red-600">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-base md:text-lg font-bold text-gray-900 leading-tight">Immediate Actions Needed</h2>
            <p className="text-xs text-red-600/90">Do these first to stop the damage</p>
          </div>
        </div>
        <ol className="divide-y divide-gray-100">
          {actions.map((action, i) => (
            <li key={i} className="flex items-start gap-3 px-4 py-3 md:px-5">
              <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-red-600 text-xs font-bold text-white tabular-nums">
                {i + 1}
              </span>
              <p className="text-sm text-gray-700 leading-relaxed text-left">{action}</p>
            </li>
          ))}
        </ol>
      </div>
    )
  }
  return (
    <div className={cn("max-w-3xl border border-gray-200 bg-white overflow-hidden", CARD_RADIUS)}>
      <h2 className="border-b border-gray-100 px-4 py-3 text-sm font-semibold text-gray-900 md:px-5">Recommended actions</h2>
      <ol className="divide-y divide-gray-100">
        {actions.map((action, i) => (
          <li key={i} className="flex items-start gap-3 px-4 py-3 md:px-5">
            <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600 tabular-nums">
              {i + 1}
            </span>
            <p className="text-[15px] leading-relaxed text-gray-800 text-left">{action}</p>
          </li>
        ))}
      </ol>
    </div>
  )
}

// Saved-plan analytics context for an anonymous diagnosis ask. Shared by the
// gate digest and the actions save strip so both report the same plan identity.
function diagnosisSavedPlanContext({
  result,
  savedAnalysisId,
  entryPath,
  primaryProblemName,
}: {
  result: DiagnosisResult
  savedAnalysisId?: string | null
  entryPath: string
  primaryProblemName: string
}): SavedPlanAnalyticsContext {
  return {
    analysisId: savedAnalysisId || undefined,
    mode: "diagnose",
    planType: "treatment",
    outcome: result.outcome || "problems",
    entryPath,
    subject: primaryProblemName || undefined,
  }
}

// In-flow save ask directly under the free first-aid steps. The gate itself
// sits in the hero grid and only ~10% of viewers ever engage it, while the
// floating sticky bar converts at ~1.7% (edge blindness) — so this second ask
// is deliberately IN the reading flow, at the moment the free value has just
// been delivered. Google goes straight to OAuth; "Email it" hands off to the
// gate's own email field rather than opening a competing input.
//
// Below-lg only. The hero grid is single-column until lg, so there the gate has
// scrolled away and this is a genuine second ask; at lg+ the gate is the right
// column and both land in one viewport, reading as a duplicate CTA. Hidden via
// CSS rather than unmounted so SSR stays stable — and because a display:none
// root never trips the IntersectionObserver, desktop logs no strip impressions.
function DiagnosisActionsSaveStrip({
  problemName,
  savedPlanContext,
  redirectUrl,
  onBeforeOAuth,
  onEmailIt,
}: {
  problemName: string
  savedPlanContext: SavedPlanAnalyticsContext
  redirectUrl?: string
  onBeforeOAuth?: () => void
  onEmailIt: () => void
}) {
  const impressionRef = useImpressionEvent<HTMLDivElement>(
    ACTIONS_STRIP_SOURCE,
    savedPlanEventProperties(savedPlanContext, { problem: problemName || undefined }),
    { emit: analytics.ctaSurfaceShown },
  )

  // Emitted here rather than through the host's onPrimaryCtaClick: that handler
  // fires the same event without a location, so routing the strip through it
  // would double-count and leave the strip indistinguishable from the gate.
  const trackStripCta = (method: "oauth" | "email") => {
    trackEvent("result_primary_cta_clicked", {
      method,
      cta_location: ACTIONS_STRIP_SOURCE,
      source: ACTIONS_STRIP_SOURCE,
      entry_path: savedPlanContext.entryPath,
      problem: problemName || undefined,
      analysis_id: savedPlanContext.analysisId,
    })
  }

  // Deliberately does NOT retire the strip: the button the user just pressed
  // owns the loading state until the OAuth redirect takes over.
  const handleOAuth = () => {
    trackStripCta("oauth")
    stashEmailCaptureAttribution(ACTIONS_STRIP_SOURCE)
    onBeforeOAuth?.()
  }

  const handleEmail = () => {
    trackStripCta("email")
    onEmailIt()
  }

  return (
    <div
      ref={impressionRef}
      className={cn(
        "max-w-3xl border border-green-200 bg-white p-4 md:px-5 lg:hidden",
        CARD_RADIUS,
        CARD_SHADOW,
      )}
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-5">
        <p className="min-w-0 flex-1 text-sm leading-relaxed text-gray-700">
          That&apos;s the free first aid. The full plan adds every treatment step, product
          picks, and prevention.{" "}
          <span className="font-semibold text-gray-900">Save it free.</span>
        </p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:flex-shrink-0">
          <SocialLoginButtons
            placement="diagnosis_actions_save_strip"
            only="google"
            redirectTo={redirectUrl}
            savedPlanContext={savedPlanContext}
            googleLabel="Save with Google"
            onBeforeRedirect={handleOAuth}
          />
          <Button
            onClick={handleEmail}
            className="w-full min-h-[48px] rounded-xl bg-linear-to-r from-green-600 to-emerald-600 px-5 font-semibold text-white shadow-lg transition hover:from-green-700 hover:to-emerald-700 hover:shadow-xl"
          >
            Email it
          </Button>
        </div>
      </div>
    </div>
  )
}

// Healthy-lawn conversion unit. A clean bill of health is a peak moment with
// nothing to fix, so instead of a dead-end we dangle ongoing, ZIP-personalized
// value: this month's upkeep tips (derived client-side from the ZIP) + an
// email/OAuth ask for "the right task every month, timed to your ZIP". Anonymous
// users get the capture (ResultEmailCaptureCard self-hides for signed-in users,
// and we gate the OAuth lane on isAnonymous too); everyone gets the seasonal card
// and the demoted "identify your grass" upgrade path.
function getHealthyUpkeepPlan(zipCode: string) {
  const region = zipCode ? getRegionFromZipCode(zipCode) : ""
  const regionLabel = region || "your area"
  const defaultGrass = region ? REGION_DEFAULT_GRASS[region] : undefined
  const monthName = getMonthName(getCurrentMonthIndex())
  const allTips = getAllSeasonalTips(defaultGrass)
  const highTips = allTips.filter((tip) => tip.priority === "high")

  return {
    regionLabel,
    monthName,
    tips: (highTips.length >= 2 ? highTips : allTips).slice(0, 3),
  }
}

function HealthyLawnUpkeep({
  zipCode,
  isAnonymous,
  savedAnalysisId,
  redirectUrl,
  onBeforeOAuth,
  onSwitchToIdentify,
  switchToIdentifyHref,
  whyRow,
}: {
  zipCode: string
  isAnonymous: boolean
  savedAnalysisId?: string | null
  redirectUrl?: string
  onBeforeOAuth?: () => void
  onSwitchToIdentify: (event?: React.MouseEvent<HTMLElement>) => void
  switchToIdentifyHref?: string
  whyRow?: React.ReactNode
}) {
  // Deterministic high-priority tips (NOT the random getSeasonalTip) so there's
  // no hydration drift; fall back to the full list if fewer than 2 are "high".
  const { regionLabel, monthName, tips } = getHealthyUpkeepPlan(zipCode)
  const handleHealthyOAuth = () => {
    stashEmailCaptureAttribution("diagnosis_healthy")
    onBeforeOAuth?.()
  }

  return (
    <div className="space-y-6">
      {isAnonymous && (
        <div className="space-y-3">
          <ResultEmailCaptureCard
            source="diagnosis_healthy"
            toolDisplayName="monthly lawn tips"
            headline={`Get monthly tips for ${regionLabel} lawns`}
            subcopy={`One email a month with what ${regionLabel} lawns need as the seasons change.`}
            successSubcopy={`You're in. A welcome email is on its way, then one email a month with seasonal lawn tips.`}
            zipCode={zipCode || null}
          />
          <div className="flex items-center gap-3 text-xs font-medium text-gray-400">
            <span className="h-px flex-1 bg-gray-200" />
            or continue with
            <span className="h-px flex-1 bg-gray-200" />
          </div>
          <SocialLoginButtons
            placement="diagnosis_healthy"
            redirectTo={redirectUrl}
            onBeforeRedirect={handleHealthyOAuth}
            savedPlanContext={{
              analysisId: savedAnalysisId || undefined,
              mode: "diagnose",
              planType: "monitoring",
              outcome: "healthy",
              entryPath: "diagnosis_healthy",
              subject: "Healthy lawn",
            }}
            compact
          />
        </div>
      )}

      {/* Seasonal upkeep — ZIP-personalized "what to do this month" */}
      {whyRow}

      {tips.length > 0 && (
        <Card className="border-gray-200">
          <CardContent className="p-5 md:p-6">
            <div className="flex items-center gap-2.5 mb-3">
              <span className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-green-50 text-green-600">
                <CalendarDays className="h-4 w-4" />
              </span>
              <h3 className="text-base font-bold text-gray-900 leading-tight">
                In {monthName}, keep {regionLabel} lawns healthy
              </h3>
            </div>
            <ul className="space-y-2.5">
              {tips.map((t, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700 leading-relaxed">
                  <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                  {t.tip}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Demoted identify cross-sell — the upgrade path to an exact plan */}
      <div className="text-center">
        {switchToIdentifyHref ? (
          <Link
            href={switchToIdentifyHref}
            onClick={onSwitchToIdentify}
            className="inline-flex items-center gap-1.5 min-h-[44px] px-3 text-sm font-semibold text-green-700 underline decoration-2 underline-offset-4 hover:text-green-800 transition-colors"
          >
            <Search className="w-4 h-4" />
            Want an exact plan? Identify your grass
          </Link>
        ) : (
          <button
            type="button"
            onClick={onSwitchToIdentify}
            className="inline-flex items-center gap-1.5 min-h-[44px] px-3 text-sm font-semibold text-green-700 underline decoration-2 underline-offset-4 hover:text-green-800 transition-colors"
          >
            <Search className="w-4 h-4" />
            Want an exact plan? Identify your grass
          </button>
        )}
      </div>
    </div>
  )
}

function TreatmentSection({
  recommendation,
  defaultExpanded = false,
  hideBestWindow = false,
  showProducts = true,
}: {
  recommendation: TreatmentRecommendation
  defaultExpanded?: boolean
  hideBestWindow?: boolean
  showProducts?: boolean
}) {
  const [expanded, setExpanded] = useState(defaultExpanded)

  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-green-50/40 transition-colors min-h-[44px]"
      >
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-green-50 text-green-600">
          <Sprout className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h4 className="font-semibold text-gray-900 leading-tight">{recommendation.problemName}</h4>
          {/* Best-window chip is suppressed on the lead treatment — the hero's
              step-1 card already shows it, so this avoids restating it. */}
          {!hideBestWindow && showProducts && (
            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700">
              <CalendarDays className="h-3 w-3" />
              Best window: {recommendation.bestTreatmentWindow}
            </span>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-5 border-t border-gray-100">
          {/* Products — icon-led rows with type + cost + timing chips (recognition
              over the old plain "type - Apply timing" line) */}
          {showProducts && recommendation.products.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center gap-1.5 mb-2.5">
                <Package className="h-3.5 w-3.5 text-gray-400" />
                <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Recommended products</h5>
              </div>
              <div className="space-y-2">
                {recommendation.products.map((product, i) => (
                  <div key={i} className="flex items-start gap-3 rounded-lg border border-gray-100 bg-gray-50/60 p-3">
                    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-gray-100 bg-white text-green-600">
                      <Package className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        {/* Link the product name to its verified affiliate URL
                            when the curated catalog has a match (e.g. Milorganite,
                            Scotts); otherwise plain text (non-Amazon brands like
                            Lesco stay unlinked, per the affiliate rules). The AI's
                            treatment products carry no ASIN, so this bridges them
                            to the same verified catalog the blog auto-linker uses. */}
                        {(() => {
                          const affiliate = findAffiliateEntry(product.name)
                          return affiliate ? (
                            <TrackedAffiliateLink
                              href={affiliate.affiliateUrl}
                              placement="diagnosis-treatment-link"
                              productName={product.name}
                              articleSlug="diagnosis-result"
                              position={i + 1}
                              className="text-sm font-semibold text-green-700 leading-tight hover:underline"
                            >
                              {product.name}
                            </TrackedAffiliateLink>
                          ) : (
                            <span className="text-sm font-semibold text-gray-900 leading-tight">{product.name}</span>
                          )
                        })()}
                        {product.costEstimate && (
                          <span className="flex-shrink-0 text-xs font-medium text-gray-500">{product.costEstimate}</span>
                        )}
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700">
                          {product.type}
                        </span>
                        <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
                          <Clock className="h-3 w-3 flex-shrink-0" />
                          {product.applicationTiming}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cultural Practices — checklist rows */}
          {recommendation.culturalPractices.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-2.5">
                <Wrench className="h-3.5 w-3.5 text-gray-400" />
                <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Cultural practices</h5>
              </div>
              <ul className="space-y-2">
                {recommendation.culturalPractices.map((practice, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600">
                    <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                    {practice}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Organic Alternatives — leaf-marked rows */}
          {showProducts && recommendation.organicAlternatives.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-2.5">
                <Leaf className="h-3.5 w-3.5 text-gray-400" />
                <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Organic alternatives</h5>
              </div>
              <ul className="space-y-2">
                {recommendation.organicAlternatives.map((alt, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600">
                    <Leaf className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                    {alt}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Cross-sell back to the identify flow — mirrors ResultsDisplay's diagnose
// cross-sell (results-display.tsx) but pointed the other direction. The
// diagnosis page already has the user's photo, so it offers a one-tap re-run
// in identify mode (no new upload).
function IdentifyCrossSell({
  uploadedImage,
  onSwitchToIdentify,
  switchToIdentifyHref,
}: {
  uploadedImage: string
  onSwitchToIdentify: (event?: React.MouseEvent<HTMLElement>) => void
  switchToIdentifyHref?: string
}) {
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
      {/* Mobile: image + text share a row; sm:contents flattens for desktop. */}
      <div className="flex items-center gap-3 sm:contents">
        {uploadedImage && (
          <img
            src={uploadedImage}
            alt="Your lawn photo"
            className="w-12 h-12 rounded-lg object-cover border border-gray-200 flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0 text-left">
          <p className="text-sm font-semibold text-gray-900 leading-snug">
            Want to know your grass type too?
          </p>
          <p className="text-xs text-gray-500 leading-snug mt-0.5">
            Identify the same photo to get a personalized 12-month care plan. No new upload needed.
          </p>
        </div>
      </div>
      {switchToIdentifyHref ? (
        <Button asChild variant="outline" className="w-full justify-center sm:w-auto sm:flex-shrink-0 min-h-[44px] border-green-200 text-green-700 hover:bg-green-50 hover:text-green-800 font-semibold">
          <Link href={switchToIdentifyHref} onClick={onSwitchToIdentify}>
            <Search className="w-4 h-4 mr-1.5" aria-hidden="true" />
            Identify this grass
          </Link>
        </Button>
      ) : (
        <Button
          variant="outline"
          onClick={onSwitchToIdentify}
          className="w-full justify-center sm:w-auto sm:flex-shrink-0 min-h-[44px] border-green-200 text-green-700 hover:bg-green-50 hover:text-green-800 font-semibold"
        >
          <Search className="w-4 h-4 mr-1.5" aria-hidden="true" />
          Identify this grass
        </Button>
      )}
    </div>
  )
}

export function DiagnosisResults({
  result,
  uploadedImage,
  zipCode = "",
  savedAnalysisId = null,
  funnelId = null,
  attemptId = null,
  originLawnId = null,
  selectedLawnId = null,
  zipMatch = null,
  onReset,
  onRefineWithSupportingPhoto,
  refinementLocked = false,
  refinementPending = false,
  refinementPolicy,
  onRefinementGateShown,
  onSwitchToIdentify,
  switchToIdentifyHref,
  user,
  authLoading = false,
  redirectUrl,
  onBeforeOAuth,
  carePlanEmail = "",
  carePlanEmailError = "",
  emailSuggestion = null,
  isSubmittingCarePlan = false,
  emailGateCompleted = false,
  timelineRedirectUrl,
  onEmailChange,
  onEmailKeyPress,
  onEmailSubmit,
  onPrimaryCtaClick,
  onAcceptEmailSuggestion,
  onDismissEmailSuggestion,
  onSeeCarePlan,
  entryPath = "diagnose",
  isPremium = false,
  subscriptionLoading = false,
  className,
}: DiagnosisResultsProps) {
  const hasSevereProblems = result.problems.some(p => p.severity === 'severe')
  const hasImmediateActions = result.immediateActions.length > 0
  const hasProblems = result.problems.length > 0
  const primary = result.problems[0]
  // The weed guide link is the established deep link and stays the only one
  // shown when it applies; the condition link covers the findings it cannot
  // reach (environmental and disease prose the server never resolved to a slug).
  const showsWeedGuideLink = Boolean(
    primary?.type === 'weed' && primary.matchedSlug && weedPageDisplayName(primary.matchedSlug),
  )
  // Same contract for pests, except the diagnosis emits PEST_CHARACTERISTICS
  // ids and white grubs are two ids sharing one page, so the slug has to come
  // through pestSlugForKey rather than being used verbatim.
  const pestGuideSlug =
    primary?.type === 'pest' && primary.matchedSlug ? pestSlugForKey(primary.matchedSlug) : undefined
  const conditionNextStep =
    showsWeedGuideLink || pestGuideSlug ? null : getDiagnosisNextStep(primary)
  const isAnonymous = !user
  // When the chemical-safety validator itself throws, the analyze route fails
  // closed: it empties `immediateActions` and every treatment `products` array
  // and stamps this status (app/api/ai/analyze-grass/route.ts). Without a
  // branch those sections simply do not render and the screen looks like the
  // model had nothing to say, so the result has to state why they are missing.
  const safetyValidatorFailedClosed =
    result.chemicalSafety?.status === 'validator_error_cultural_only'
  const isFailedAnalysis = !hasProblems && result.confidence === 0
  const imageQuality = result.imageQuality.trim().toLowerCase()
  const legacyHealthyEvidence =
    !hasProblems &&
    result.confidence >= 70 &&
    imageQuality === 'good'
  const isHealthy =
    !hasProblems &&
    result.confidence >= 70 &&
    imageQuality === 'good' &&
    (result.outcome === 'healthy' || (result.outcome === undefined && legacyHealthyEvidence))
  const topProblemConfidence = result.problems[0]?.confidence ?? 0
  const hasActionableProblemEvidence =
    hasProblems &&
    (result.outcome === undefined || result.outcome === 'problems') &&
    imageQuality !== 'poor' &&
    result.confidence >= 60 &&
    topProblemConfidence >= 70
  const isInconclusive =
    result.outcome === 'inconclusive' ||
    (hasProblems && !hasActionableProblemEvidence) ||
    (!hasProblems && !isFailedAnalysis && !isHealthy)
  const refinementReason: RefinementReason = refinementPolicy?.reason
    ?? (result.quality?.abstentionReason === 'poor_image' ? 'poor_image' : 'inconclusive')
  // At the photo ceiling the server policy closes the offer; without a policy
  // (saved pages) stay permissive — the hop re-resolves server-side.
  const refinementAvailable = Boolean(onRefineWithSupportingPhoto) &&
    (refinementPolicy ? refinementPolicy.available : true)
  // Benefit-led CTA copy for the two-answers-disagree case, mirroring the
  // identify label helper in results-display. The conflict arm sells a
  // tiebreaker: the replacement rescan behind the disagreement left the first
  // photo behind, so the run that follows is this photo plus one new view.
  const refinementCtaBenefit = refinementReason === 'conflicting_rescan'
    ? 'Add one more angle to break the tie'
    : 'Add a photo to pin down the problem'
  // The visible label sells the benefit; the lock glyph plus a screen-reader
  // suffix carry the account requirement.
  const refinementCtaLabel = refinementCtaBenefit
  // Benefit-led like the reason CTA above it: the bare "Add another angle"
  // named the action and left the payoff inside the gate panel, which only an
  // anonymous tap ever opens.
  const retryAdditiveLabel = 'Add another angle to confirm this'
  // The one view that would settle this run, in the same order the server
  // resolves it: the policy's ask, then the model's own recommended photo, then
  // its first named missing cue, then the per-mode fallback. An inconclusive
  // screen that cannot name the missing view sends the user back to guess.
  const inconclusiveNextPhotoAsk = (() => {
    const policyAsk = refinementPolicy?.nextPhotoGuidance?.trim()
    if (policyAsk) return policyAsk
    const recommended = result.quality?.recommendedNextPhoto?.trim()
    if (recommended) return recommended
    const missing = result.quality?.missingEvidence
      ?.map((item) => item.trim())
      .find((item) => item.length > 0)
    if (missing) return `Add a close-up that shows ${missing}.`
    return REFINEMENT_GUIDANCE_FALLBACK.diagnose
  })()
  // Two diagnoses that disagree survive a settled result, so the conflict CTA
  // gets a render site in the problems-found branch as well.
  const showsConflictCta = refinementAvailable && refinementReason === 'conflicting_rescan'
  const refinementAtCeiling = refinementPolicy != null &&
    refinementPolicy.slotsRemaining === 0 &&
    !refinementPolicy.requiresAuth &&
    !refinementPolicy.requiresUpgrade
  // `refinementAvailable` carries the server policy's own close: a settled
  // result whose policy says available:false has no offer left to make, and a
  // saved page with no policy stays permissive.
  // Server truth: once the anonymous replacement allowance for this root is
  // spent, the only honest next step is the gated additive lane. A saved page
  // with no live policy keeps the offer up; the server cap is the backstop.
  const hideReplacementRetry = refinementLocked &&
    refinementPolicy?.replacementRescansRemaining === 0
  // Matches the identify presenter (results-display.tsx): the additive retry is
  // a user-initiated option, not a server recommendation, so it deliberately
  // does NOT read `policy.available`. `available` is
  // `reason !== null && ceilingHasRoom`, so a settled result closes it purely
  // for want of a server-named reason while the server would still accept the
  // run. The real close is the photo ceiling, and `refinementAtCeiling` already
  // models it: at `imageCount === ceiling` the policy reports slotsRemaining 0
  // with neither requiresAuth nor requiresUpgrade set.
  // Same defect as the identify presenter: refinementAtCeiling requires
  // !requiresUpgrade, so a registered_free caller at their photo cap reads as
  // "not at the ceiling" and the offer renders into a server 403. Cap room is
  // the honest test; a saved page with no policy stays permissive.
  // A LOCKED visitor is measured against the cap they get after signing in,
  // not their anonymous cap of 1: the offer they see is the account ask, and
  // the free tier reads up to 3. Testing 1 < 1 here removed the additive lane
  // from every confident anonymous result (live impressions fell from ~8/hour
  // to ~0 the hour it shipped) while the saved page, which carries no policy,
  // kept offering it.
  const hasPhotoCapRoom =
    refinementPolicy == null ||
    refinementLocked ||
    refinementPolicy.imageCount < refinementPolicy.photoCap
  const canRetryAdditive = Boolean(onRefineWithSupportingPhoto) &&
    !refinementAtCeiling &&
    !showsConflictCta &&
    hasPhotoCapRoom
  // Every refinement tap goes through here. The offer stays mounted while the
  // host opens its sign-in gate, so without a guard a double tap starts two
  // refinements and double-counts `refinement_started` against its own
  // impression denominator. The time window is the guard rather than a latch,
  // so dismissing the gate ("Keep my current result") leaves the CTA live.
  const lastRefinementStartRef = useRef(0)
  const startRefinement = (trigger: RefinementTrigger) => {
    if (refinementPending) return
    const now = Date.now()
    if (now - lastRefinementStartRef.current < REFINEMENT_TAP_GUARD_MS) return
    lastRefinementStartRef.current = now
    onRefineWithSupportingPhoto?.(trigger)
  }
  const handleRetryAdditive = () => startRefinement('retry_additive')
  const refinementChain = refinementPolicy?.chain ?? null
  // What each photo of this root actually returned. Renders nothing for a
  // single scan. Ordinals come from the server so a dropped unnamed hop cannot
  // renumber the rest.
  const refinementChainStrip = refinementChain && refinementChain.length >= 2 ? (
    <p className="mb-4 text-center text-[13px] leading-snug text-gray-600">
      {refinementChain
        .map((entry) => `Photo ${entry.position}: ${entry.label}${entry.confidence != null ? ` ${entry.confidence}%` : ''}`)
        .join(' \u00b7 ')}
    </p>
  ) : null
  // Impression for whichever offer actually rendered. The inconclusive and
  // failed branches carry their own CTAs, so the additive retry lane counts
  // only on the settled screens that render it.
  const refinementImpressionTrigger: RefinementTrigger | null =
    refinementAvailable && (isInconclusive || showsConflictCta)
      ? refinementReason
      : canRetryAdditive && !isInconclusive && !isFailedAnalysis
        ? 'retry_additive'
        : null
  // Viewport-gated: the offer can sit hundreds of pixels below the fold, and a
  // mount-time impression counted every one of those as seen. The registry is
  // per-instance so a new result still re-arms it.
  const refinementImpressions = useRef<Set<string>>(new Set()).current
  const refinementOfferRef = useInViewportImpression(
    refinementImpressions,
    Boolean(refinementImpressionTrigger),
    `${savedAnalysisId ?? 'pending'}:${refinementImpressionTrigger ?? 'none'}`,
    () => {
      if (!refinementImpressionTrigger) return
      onRefinementGateShown?.(refinementImpressionTrigger)
    },
  )
  const deterministicTreatmentFallback =
    hasActionableProblemEvidence && result.treatmentRecommendations.length === 0
      ? getConditionTreatmentFallback(primary)
      : null
  const treatmentRecommendations = deterministicTreatmentFallback
    ? [deterministicTreatmentFallback]
    : result.treatmentRecommendations
  // Hoisted above `offeredActions`: the second-look gate below is part of the
  // offered set, so it has to be computable before the impression fires.
  const hasTreatments = treatmentRecommendations.length > 0
  const canShowGuidance = hasTreatments && hasActionableProblemEvidence
  const canUseVerifiedTreatment = canShowGuidance && result.hostGrassVerified === true
  // Treatment GUIDANCE is suppressed when the product safety validator failed
  // closed; the conversion/save card is not, because the notice withholds
  // treatment, not the finding.
  //
  // DEFENSIVE, not a fix for an observed bug. The reasoning: the deterministic
  // client fallback in data/condition-product-picks.ts carries culturalPractices
  // that name chemicals ("use a lawn fungicide", "use a lawn insect-control
  // product", "spot-treat with a post-emergent product"), and emptying
  // `products` server-side does not remove that prose. If this block renders on
  // a fail-closed result it contradicts the notice above it. I could NOT
  // reproduce it rendering (neither in the unit harness nor in the browser), so
  // there is no regression test here — a passing one would be vacuous. The
  // observed and separately-tested contradiction was the curated product shelf.
  // If you later find the path that renders this, add the test then.
  const canShowTreatmentGuidance = canShowGuidance && !safetyValidatorFailedClosed
  const canUseVerifiedTreatmentSafely = canUseVerifiedTreatment && !safetyValidatorFailedClosed
  const isHighConfidenceDiagnosis = isHighConfidenceScore(result.confidence)

  // The full email-capture flow is only available when the host page threads
  // its handlers through. The live /diagnose caller wires email submit; the
  // saved /result caller also wires it. When absent (defensive), the anonymous
  // CTA falls back to OAuth-only, exactly like the pre-rebuild behavior.
  const hasEmailCapture = Boolean(onEmailChange && onEmailKeyPress && onEmailSubmit)

  // Names the diagnosed problem in the digest ask ("Your Brown Patch treatment
  // plan"). Always present when the gate renders (problems + treatments exist).
  // (The diagnosis-gate-v1 + diagnosis_cta_copy_v1 experiments were concluded
  // 2026-07-05 — 0/52 conversions across arms — and the gate was consolidated
  // into DiagnosisResultDigest; flag reads removed.)
  const primaryProblemName = result.problems[0]?.name?.trim() || ""
  const [whyExpanded, setWhyExpanded] = useState(false)
  const [reviewRequestedByFeedback, setReviewRequestedByFeedback] = useState(false)
  // The gate's email field, so the hero CTA and the actions strip can send the
  // caret there instead of leaving the user to find it after the scroll.
  const gateEmailInputRef = useRef<HTMLInputElement | null>(null)
  // The gate's email lane is collapsed behind its "get it by email" link, so any
  // CTA here that means "email it to me" has to open it before the caret can
  // land. Emitting the same event the card's own reveal emits keeps the funnel
  // whole no matter which control opened the lane. The payloads match because
  // this gate never passes `grassName` (a diagnosis names a problem, not a
  // species), so the card's `grassType` extra resolves to undefined too — pass
  // grass here as well if that ever changes, or the two paths split the event.
  const [gateEmailRevealed, setGateEmailRevealed] = useState(false)
  const revealGateEmail = () => {
    if (gateEmailRevealed) return
    setGateEmailRevealed(true)
    analytics.softEmailCaptureOpened(GATE_SOURCE)
  }
  // Once the user is working the gate itself, the secondary asks have done their
  // job and only add noise. Set from the gate's own callbacks (never mirrored
  // from props in an effect).
  const [gateEngaged, setGateEngaged] = useState(false)
  const diagnosisLandingTrackedKeyRef = useRef<string | null>(null)
  const evidenceImages = useMemo(() => (
    Array.isArray(result.imageUrls) && result.imageUrls.length > 0
      ? result.imageUrls.filter((url): url is string => typeof url === 'string' && url.length > 0)
      : uploadedImage ? [uploadedImage] : []
  ), [result.imageUrls, uploadedImage])
  const requestedEvidenceCover = Number.isInteger(result.evidenceCoverImageIndex)
    ? result.evidenceCoverImageIndex as number
    : 0
  const safeEvidenceCover = requestedEvidenceCover >= 0 && requestedEvidenceCover < evidenceImages.length
    ? requestedEvidenceCover
    : 0
  const [selectedEvidenceImageIndex, setSelectedEvidenceImageIndex] = useState(safeEvidenceCover)
  useEffect(() => {
    setSelectedEvidenceImageIndex(safeEvidenceCover)
  }, [safeEvidenceCover, savedAnalysisId])
  const selectedEvidenceImage = evidenceImages[selectedEvidenceImageIndex] ?? uploadedImage
  const resolvedOutcome = isFailedAnalysis
    ? 'failed'
    : isInconclusive
      ? 'inconclusive'
      : isHealthy
        ? 'healthy'
        : 'problems'
  const diagnosisGatingReasonKey = [
    isFailedAnalysis ? 'analysis_failed' : null,
    isInconclusive ? 'inconclusive' : null,
    imageQuality === 'fair' || imageQuality === 'poor' ? `image_quality:${imageQuality}` : null,
    result.confidence < 60 ? 'low_result_confidence' : null,
    hasProblems && topProblemConfidence < 70 ? 'low_finding_confidence' : null,
    hasSevereProblems ? 'severe_problem' : null,
  ].filter((reason): reason is string => Boolean(reason)).join(',')
  const diagnosisFunnelProperties = useMemo<EventProperties>(() => ({
      ...analysisFunnelEnvelope({
        funnelId: funnelId ?? (savedAnalysisId ? `saved_${savedAnalysisId}` : null),
        attemptId,
        analysisId: savedAnalysisId,
        originLawnId,
        selectedLawnId,
        zipMatch,
        mode: 'diagnose',
        source: entryPath === 'saved_result' ? 'saved_result' : 'web_diagnosis_result',
        imageRoles: evidenceImages.map((_, index) => index === 0 ? 'primary' : 'supporting'),
        rawConfidence: result.quality?.rawConfidence ?? result.confidence,
        displayConfidence: result.quality?.displayConfidence ?? result.confidence,
        gatingReasons: diagnosisGatingReasonKey,
        safetyProof: result.hostGrassVerified === true
          ? 'host_grass_verified'
          : 'host_grass_unverified',
        model: result.model,
        promptVersion: result.quality?.promptVersion,
        promptStrategy: result.promptStrategy,
        pipelineVersion: result.pipelineVersion ?? result.quality?.pipelineVersion,
        authState: user ? 'authenticated' : 'anonymous',
        actualDestination: entryPath === 'saved_result'
          ? `/result/${savedAnalysisId ?? 'unknown'}`
          : '/diagnose#results',
      }),
      outcome: resolvedOutcome,
      problem_count: result.problems.length,
      image_quality: result.imageQuality,
    }), [
      attemptId,
      diagnosisGatingReasonKey,
      entryPath,
      evidenceImages,
      funnelId,
      originLawnId,
      resolvedOutcome,
      result.confidence,
      result.hostGrassVerified,
      result.imageQuality,
      result.model,
      result.pipelineVersion,
      result.problems.length,
      result.promptStrategy,
      result.quality?.displayConfidence,
      result.quality?.pipelineVersion,
      result.quality?.promptVersion,
      result.quality?.rawConfidence,
      savedAnalysisId,
      selectedLawnId,
      user,
      zipMatch,
    ])

  // Landing + shown instrumentation. `diagnosis_result_landed` fires for every
  // rendered result so a redesign is measurable; `diagnosis_gate_shown` stays
  // for the anon-with-treatments funnel (variant fields dropped with the flags).
  useEffect(() => {
    if (authLoading) return
    const landingKey = [
      diagnosisFunnelProperties.funnel_id,
      diagnosisFunnelProperties.attempt_id,
      diagnosisFunnelProperties.analysis_id,
      resolvedOutcome,
    ].join(':')
    if (diagnosisLandingTrackedKeyRef.current === landingKey) return
    diagnosisLandingTrackedKeyRef.current = landingKey
    trackEvent('diagnosis_result_landed', {
      ...diagnosisFunnelProperties,
      has_severe: hasSevereProblems,
      treatment_count: treatmentRecommendations.length,
    })
    if (isFailedAnalysis) {
      trackEvent('diagnosis_failed_shown', {
        ...diagnosisFunnelProperties,
        confidence: result.confidence,
      })
    } else if (isInconclusive) {
      trackEvent('diagnosis_inconclusive_shown', {
        ...diagnosisFunnelProperties,
        confidence: result.confidence,
        image_quality: result.imageQuality,
        problem_count: result.problems.length,
      })
    } else if (isHealthy) {
      trackEvent('diagnosis_healthy_shown', {
        ...diagnosisFunnelProperties,
        confidence: result.confidence,
      })
    } else {
      if (isAnonymous && treatmentRecommendations.length > 0) {
        trackEvent('diagnosis_gate_shown', {
          ...diagnosisFunnelProperties,
          problem_count: result.problems.length,
          has_severe: hasSevereProblems,
        })
      }
    }
  }, [
    authLoading,
    diagnosisFunnelProperties,
    isAnonymous,
    treatmentRecommendations.length,
    result.confidence,
    result.imageQuality,
    result.problems.length,
    resolvedOutcome,
    hasSevereProblems,
    isFailedAnalysis,
    isHealthy,
    isInconclusive,
  ])

  // Result-state funnel. `resultStateShown` answers "which of the four diagnose
  // screens did the user actually land on, and what did it offer"; the paired
  // `resultNextActionClicked` answers whether they took any of it. Both carry
  // the same `state` / `from_state`, which is what makes the pair a funnel.
  const resultState: ResultStateId = isFailedAnalysis
    ? 'diagnose_failed'
    : isInconclusive
      ? 'diagnose_inconclusive'
      : isHealthy
        ? 'diagnose_healthy'
        : 'diagnose_problems'
  // A saved result carries no live attempt id, so it borrows the analysis id
  // the same way `diagnosisFunnelProperties` borrows it for `funnel_id`.
  const resultStateAttemptId = attemptId ?? (savedAnalysisId ? `saved_${savedAnalysisId}` : null)
  const resultStateFunnelId = funnelId ?? (savedAnalysisId ? `saved_${savedAnalysisId}` : null)
  const resultStateAuthState: 'anonymous' | 'authenticated' | 'unknown' =
    authLoading ? 'unknown' : user ? 'authenticated' : 'anonymous'
  // The one paid continuation a diagnosis has earned. A named problem plus a
  // treatment plan raises exactly one follow-up question: did the fix work?
  // Answering it means scanning the same patch again, which is what the shared
  // monthly allowance rations, so "recheck as often as you need" continues this
  // state instead of bolting a generic upgrade onto it. Deliberately NOT the
  // reminder offer the identify result carries: two states, two continuations.
  //
  // Healthy is excluded on purpose. It establishes an absence, so there is no
  // fix to recheck and no species named (diagnose carries no grass type), which
  // leaves nothing specific to continue. Inconclusive and failed are excluded
  // for the same reason plus the Wave 2 recovery paths a paid ask would undercut.
  // Fail closed on entitlement, and never sell a recheck of a fix we are
  // refusing to prescribe. `isPremium` arrives from the host, but the saved
  // -result host has no usage envelope, so AuthContext is the second source
  // and the subscription must have settled (it resolves at once when signed
  // out). safetyValidatorFailedClosed means the validator threw and the route
  // emptied every action and product: the screen already says we are holding
  // the treatment steps back, so a "recheck this fix" upsell contradicts it.
  const knownNotPremium =
    !subscriptionLoading && !isPremium
  const showPremiumRecheckContinuation =
    !isFailedAnalysis &&
    !isInconclusive &&
    !isHealthy &&
    !safetyValidatorFailedClosed &&
    knownNotPremium
  const premiumContinuationHref = resultStateFunnelId
    ? `/pricing?funnel_id=${resultStateFunnelId}`
    : '/pricing'

  // The problems branch offers a second look whenever the result is severe,
  // unverified, low confidence, or the user just disputed it. Single source of
  // truth for the render gate and the offered set. Healthy shares the same
  // `return` in `offeredActions` but renders no review card, so it is excluded
  // here rather than in the branch.
  const showReviewRequestOffer =
    !isHealthy &&
    (reviewRequestedByFeedback ||
      hasSevereProblems ||
      (hasTreatments && !canUseVerifiedTreatment) ||
      !isHighConfidenceDiagnosis)
  const showHeroPlanCapture = isAnonymous && canShowGuidance && !emailGateCompleted

  // Only what this render actually paints. An action listed here that the
  // screen does not show turns the taken/offered rate into a fiction.
  const offeredActions = useMemo<string[]>(() => {
    if (isFailedAnalysis) {
      return ['retake_photo', ...(isAnonymous ? ['email_photo_checklist'] : [])]
    }
    if (isInconclusive) {
      return [
        refinementAvailable ? 'add_supporting_photo' : 'retake_photo',
        'request_second_look',
        'email_seasonal_plan',
        'open_tool',
        'switch_to_identify',
      ]
    }
    // Healthy and problems share this list, but the tie-breaker renders in the
    // problems branch only, so advertising it on healthy would put an action in
    // the denominator that no control there can ever answer.
    return [
      ...(showHeroPlanCapture ? ['open_treatment_plan'] : []),
      ...(showsConflictCta && !isHealthy ? ['break_the_tie'] : []),
      ...(canRetryAdditive ? ['add_supporting_photo'] : []),
      ...(hideReplacementRetry ? [] : ['retake_photo']),
      'open_tool',
      'switch_to_identify',
      ...(showPremiumRecheckContinuation ? ['premium_recheck'] : []),
      // Last, matching render order: a negative confidence answer can reveal
      // this after the impression, and appending keeps every other position
      // identical to what the impression reported.
      ...(showReviewRequestOffer ? ['request_second_look'] : []),
    ]
  }, [
    canRetryAdditive,
    hideReplacementRetry,
    isAnonymous,
    isFailedAnalysis,
    isHealthy,
    isInconclusive,
    refinementAvailable,
    showPremiumRecheckContinuation,
    showReviewRequestOffer,
    showHeroPlanCapture,
    showsConflictCta,
  ])
  // Freeze the offered list so a click can never report a position that
  // indexes a different array than the impression sent. Entitlement
  // resolving INSERTS premium_recheck mid-array, which the append-last
  // convention below cannot absorb.
  const { snapshotOfferedActions, positionOf } = useOfferedActionsSnapshot(offeredActions)
  useVisibleImpression(
    resultStateImpressionKeys,
    !authLoading && Boolean(resultStateAttemptId),
    `${resultStateAttemptId}:${resultState}`,
    () => {
      if (!resultStateAttemptId) return
      analytics.resultStateShown({
        state: resultState,
        mode: 'diagnose',
        attemptId: resultStateAttemptId,
        funnelId: resultStateFunnelId,
        analysisId: savedAnalysisId,
        offeredActions: snapshotOfferedActions(),
        confidence: result.confidence,
        problem: primaryProblemName || null,
        restored: entryPath === 'saved_result',
        authState: resultStateAuthState,
      })
    },
  )
  const trackNextAction = (action: string) => {
    if (!resultStateAttemptId) return
    analytics.resultNextActionClicked({
      action,
      fromState: resultState,
      mode: 'diagnose',
      attemptId: resultStateAttemptId,
      funnelId: resultStateFunnelId,
      analysisId: savedAnalysisId,
      position: positionOf(action),
      authState: resultStateAuthState,
    })
  }
  const handleSwitchToIdentify = (event?: React.MouseEvent<HTMLElement>) => {
    trackNextAction('switch_to_identify')
    onSwitchToIdentify(event)
  }

  // Failed analysis — unable to complete diagnosis
  if (isFailedAnalysis) {
    return (
      <section id="results-section" className={cn("relative overflow-x-hidden px-4 py-6 md:px-10 md:py-8 scroll-mt-24", className)}>
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="text-center">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-100 bg-amber-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.04em] text-amber-700 mb-3 md:mb-4">
              <AlertTriangle className="w-3 h-3" strokeWidth={2.5} />
              Photo not readable
            </div>
            <h1 className="text-2xl md:text-4xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">
              We couldn&apos;t read that photo.
            </h1>
            <p className="mt-2 md:mt-4 mx-auto max-w-md text-sm md:text-base text-gray-500 leading-relaxed">
              Try uploading a clearer, well-lit image of the affected area for better results.
            </p>
            <Button
              onClick={(event) => {
                trackNextAction('retake_photo')
                onReset(event)
              }}
              className="mt-6 bg-green-600 hover:bg-green-700 text-white min-h-[48px] px-8 text-base font-semibold"
            >
              <Camera className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </div>

          <PhotoTips />

          {isAnonymous && (
            <ResultEmailCaptureCard
              source="diagnosis_failed"
              toolDisplayName="photo checklist"
              headline="Email me the photo checklist"
              subcopy="We will send a quick checklist so your next photo diagnoses first try, plus a link to re-run it."
              successSubcopy="Checklist on the way. Come back and re-run your diagnosis anytime."
              zipCode={zipCode || null}
              onCaptured={() => trackNextAction('email_photo_checklist')}
              // Account-led here too, but framed for a failure: nothing to "save"
              // yet, so the ask is about picking the retry back up, and the OAuth
              // labels stay neutral ("Continue with"), not "Save with".
              accountLead={
                redirectUrl
                  ? {
                      redirectTo: redirectUrl,
                      headline: "Keep this diagnosis and re-run it anytime",
                      subcopy:
                        "Come back anytime to re-run your diagnosis, plus the photo checklist and seasonal tips for your ZIP.",
                      dividerLabel: "or email me the checklist",
                      googleLabel: "Continue with Google",
                      appleLabel: "Continue with Apple",
                      onBeforeOAuth: () => {
                        trackNextAction('email_photo_checklist')
                        stashEmailCaptureAttribution("diagnosis_failed")
                        onBeforeOAuth?.()
                      },
                    }
                  : undefined
              }
            />
          )}
        </div>
      </section>
    )
  }

  if (isInconclusive) {
    return (
      <section id="results-section" className={cn("relative overflow-x-hidden px-4 py-6 md:px-10 md:py-8 scroll-mt-24", className)}>
        <div className="mx-auto max-w-2xl space-y-5">
          <div className="text-center">
            <div className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-amber-100 bg-amber-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.04em] text-amber-700">
              <AlertTriangle className="h-3 w-3" strokeWidth={2.5} />
              More evidence needed
            </div>
            <h1 className="text-2xl font-extrabold leading-tight tracking-tight text-gray-900 md:text-4xl">
              We can&apos;t confirm a diagnosis from this photo.
            </h1>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-gray-600 md:text-base">
              No treatment is recommended yet. {inconclusiveNextPhotoAsk}
            </p>
            {uploadedImage && (
              <img
                src={uploadedImage}
                alt="Analyzed lawn photo"
                className="mx-auto mt-5 h-48 w-48 rounded-2xl border border-gray-200 object-cover md:h-60 md:w-60"
              />
            )}
            {refinementChainStrip && <div className="mt-4">{refinementChainStrip}</div>}
          </div>
          {/* Answer path first, offer second. 29.5% of diagnoses land here and
              the second-look CTA took 5.1% while it sat below the email ask. The
              user's live question is what is wrong with the lawn; the retake is
              the only thing on this screen that can answer it, so it leads and
              the seasonal-plan ask follows. */}
          <div className="space-y-3" ref={refinementOfferRef}>
            {/* Prefer the targeted refinement (keeps photo + ZIP, asks for one
                supporting view, quota-exempt within the rescan window) over a
                full restart when the host wires it. */}
            <Button
              type="button"
              variant="green"
              disabled={refinementAvailable && Boolean(onRefineWithSupportingPhoto) && refinementPending}
              onClick={() => {
                if (refinementAvailable && onRefineWithSupportingPhoto) {
                  trackNextAction('add_supporting_photo')
                  startRefinement(refinementReason)
                  return
                }
                trackNextAction('retake_photo')
                onReset()
              }}
              className="min-h-[52px] w-full px-6 text-base"
            >
              {refinementAvailable && refinementLocked ? (
                <Lock className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Camera className="h-5 w-5" aria-hidden="true" />
              )}
              {refinementAvailable ? refinementCtaLabel : "Add better photos"}
            </Button>
            <DiagnosisReviewRequest
              result={result}
              analysisId={savedAnalysisId}
              user={user}
              analyticsReady={!authLoading}
              reason="inconclusive"
              triggerStyle="link"
              funnelProperties={diagnosisFunnelProperties}
              onNextAction={() => trackNextAction('request_second_look')}
            />
          </div>
          <PhotoTips surface="diagnosis_inconclusive" />
          <InconclusivePlanAsk
            source="diagnosis_inconclusive"
            zipCode={zipCode || null}
            redirectTo={isAnonymous ? redirectUrl : undefined}
            onCaptured={() => trackNextAction('email_seasonal_plan')}
            onBeforeOAuth={() => {
              trackNextAction('email_seasonal_plan')
              stashEmailCaptureAttribution("diagnosis_inconclusive")
              onBeforeOAuth?.()
            }}
          />
          {/* Everything below here sits UNDER the primary ask, never beside it:
              the refine CTA stays the single primary action on this screen.
              Ordering matches the problems and healthy branches, and both the
              tool grid and the cross-sell follow every capture ask so a durable
              link can never intercept the personalized offer.
              Deliberately no product shelf: an inconclusive diagnosis has no
              confirmed problem, so there is nothing honest to sell against. */}
          <ResultToolDeeplinks
            grassType={DIAGNOSIS_GRASS_TYPE}
            zip={zipCode}
            onNextAction={() => trackNextAction('open_tool')}
          />
          <IdentifyCrossSell
            uploadedImage={uploadedImage}
            onSwitchToIdentify={handleSwitchToIdentify}
            switchToIdentifyHref={switchToIdentifyHref}
          />
          <div className="text-center">
            <AppDownloadTextLink placement="diagnose_inconclusive_result" />
          </div>
        </div>
      </section>
    )
  }

  // Healthy lawn — only an explicit verdict or strong legacy evidence may use
  // this reassuring branch. An empty problems array alone is inconclusive.
  if (isHealthy) {
    const healthyUpkeepPlan = getHealthyUpkeepPlan(zipCode)

    return (
      <section id="results-section" className={cn("relative overflow-x-hidden px-4 py-6 md:px-10 md:py-8 scroll-mt-24", className)}>
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="text-center">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-green-100 bg-green-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.04em] text-green-700 mb-3 md:mb-4 animate-scale-bounce">
              <CheckCircle className="w-3 h-3" strokeWidth={2.5} />
              Diagnosis complete
            </div>
            <h1 className="text-2xl md:text-4xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">
              Your lawn looks{" "}
              <span className="text-green-600 border-b-[3px] border-green-600 pb-0.5">
                healthy.
              </span>
            </h1>
            <p className="mt-2 md:mt-4 mx-auto max-w-2xl text-sm md:text-base text-gray-500 leading-relaxed">
              No disease, pests, weeds, or stress showing &middot; {result.confidence}% confidence
            </p>
            {/* The caveat travels with the number, not only with the footer
                disclaimer three screens down. */}
            <p className="mt-1 mx-auto max-w-2xl text-xs text-gray-400 leading-relaxed">
              {DIAGNOSIS_SCORE_CAVEAT}
            </p>
            {uploadedImage && (
              <img
                src={uploadedImage}
                alt="Analyzed lawn photo"
                className="mt-4 w-20 h-20 mx-auto rounded-xl object-cover border border-gray-200"
              />
            )}
          </div>

          {!isAnonymous && (
            <AuthenticatedPlanCard
              treatmentCount={0}
              primaryTreatment={undefined}
              firstAction={healthyUpkeepPlan.tips[0]?.tip}
              analysisId={savedAnalysisId}
              outcome="healthy"
              entryPath={entryPath}
            />
          )}

          {/* Healthy verdict + seasonal upkeep + email/OAuth capture */}
          <div id="diagnosis-plan" className="scroll-mt-24">
            <HealthyLawnUpkeep
              zipCode={zipCode}
              isAnonymous={isAnonymous}
              savedAnalysisId={savedAnalysisId}
              redirectUrl={redirectUrl}
              onBeforeOAuth={onBeforeOAuth}
              onSwitchToIdentify={handleSwitchToIdentify}
              switchToIdentifyHref={switchToIdentifyHref}
              whyRow={(
                <div className="min-w-0">
                  <button
                    type="button"
                    onClick={() => setWhyExpanded((v) => !v)}
                    aria-expanded={whyExpanded}
                    className="w-full flex items-center justify-between gap-2 rounded-xl border border-green-100 bg-green-50/60 px-4 py-3 text-left transition-colors hover:bg-green-50"
                  >
                    <span className="text-[13px] font-semibold text-green-800 leading-snug">
                      Why we say healthy
                    </span>
                    <ChevronDown
                      className={cn(
                        "w-4 h-4 flex-shrink-0 text-green-700 transition-transform",
                        whyExpanded && "rotate-180",
                      )}
                      aria-hidden="true"
                    />
                  </button>
                  {whyExpanded && (
                    <div className="mt-2 rounded-xl border border-gray-100 bg-white p-3 md:p-4 text-left">
                      <ConfidenceMeter confidence={result.confidence} />
                      <div className="mt-2 space-y-2">
                        {result.overallAssessment && (
                          <p className="text-xs md:text-[13px] text-gray-600 leading-relaxed">
                            {result.overallAssessment}
                          </p>
                        )}
                        <p className="text-xs md:text-[13px] text-gray-500 leading-relaxed">
                          Image quality: {result.imageQuality}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            />
          </div>

          {/* Another view of the same lawn is worth more read alongside the
              first than read alone, so the additive lane leads. The pair sits
              above the commerce tail: it is an action on the verdict the user
              is still reading, and at the page edge it was scanned past. */}
          <div className="mx-auto w-full max-w-md space-y-2 text-center" ref={refinementOfferRef}>
            {canRetryAdditive && (
              <Button
                type="button"
                variant="outline"
                disabled={refinementPending}
                onClick={() => {
                  trackNextAction('add_supporting_photo')
                  handleRetryAdditive()
                }}
                className="min-h-[44px] w-full rounded-full border-gray-300 font-semibold text-gray-700 hover:border-green-600 hover:bg-white hover:text-green-700"
              >
                {refinementLocked ? (
                  <Lock className="mr-2 h-4 w-4" aria-hidden="true" />
                ) : (
                  <Camera className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                {retryAdditiveLabel}{refinementLocked && <span className="sr-only">, free account</span>}
              </Button>
            )}
            {/* The free-re-run promise requires a live policy: saved pages have no
                entitlement knowledge, and promising free on a spent root would be
                false. refinementsRemaining === null means unlimited (premium). */}
            {canRetryAdditive && !refinementLocked && refinementPolicy != null && refinementPolicy.refinementsRemaining !== 0 && (
              <p className="text-xs text-gray-500">This re-run is free. It will not use another scan.</p>
            )}
            {!hideReplacementRetry && (
              <button
                type="button"
                onClick={(event) => {
                  trackNextAction('retake_photo')
                  onReset(event)
                }}
                className="inline-flex min-h-[44px] items-center justify-center px-3 text-sm text-gray-500 transition-colors hover:text-gray-700"
              >
                Try a different photo
              </button>
            )}
          </div>

          {/* ZIP-prefilled tools — keep-it-healthy, dialed to your area */}
          <ResultToolDeeplinks
            grassType={DIAGNOSIS_GRASS_TYPE}
            zip={zipCode}
            onNextAction={() => trackNextAction('open_tool')}
          />

          {/* Maintenance essentials — watering/soil shelf (well-stocked). */}
          <HealthyDiagnosisProductPicks />

          <AppDownloadCTA
            placement="diagnose_healthy_result"
            variant="card"
            intent="diagnose_lawn_problem"
            headline="Recheck your lawn from your phone"
            subhead="Use the free iPhone app to scan changes, save each result, and keep your care plan with you."
          />
        </div>
      </section>
    )
  }

  // Problems found — full parity layout (mirrors ResultsDisplay):
  //   2-col grid (verdict / CTA) + full-width sections below.
  const primarySeverity = getSeverityStyles(primary.severity)
  const extraCount = result.problems.length - 1
  const displayResult = deterministicTreatmentFallback
    ? { ...result, treatmentRecommendations }
    : result
  const canShowConversionPlan = canShowGuidance
  // The safety notice below states plainly that the treatment steps are being
  // held back. The capture ask survives — the FINDING is real and still worth
  // saving — but every surface of it that says "plan" or shows a treatment
  // step contradicts the notice, so the ask runs in a hold-aware mode: no
  // "Your treatment plan is ready", no free "Start here" step, no locked-plan
  // tease, and no full-plan pitch in the strip or the sticky bar.
  const conversionPlanOnSafetyHold = canShowConversionPlan && safetyValidatorFailedClosed
  const conditionPicks = getCompleteConditionPicks(
    primary,
    result.hostGrassVerified === true ? result.hostGrassType : undefined,
    getCurrentSeason(),
    {
      hostGrassVerified: result.hostGrassVerified === true,
      allowUnverifiedGrassProducts: primary.type === 'weed',
    },
  )
  const curatedConditionPicks = conditionPicks.source === 'curated' ? conditionPicks : null
  // The curated shelf is derived client-side from the condition, so the route's
  // fail-closed path (which empties treatmentRecommendations and every nested
  // products array) cannot reach it. Without this guard a validator failure
  // renders "we are not listing products" directly above three purchasable
  // fungicides. If the product safety check did not complete, we do not
  // recommend chemicals.
  const canShowCuratedProductShelf =
    canShowGuidance && Boolean(curatedConditionPicks) && !safetyValidatorFailedClosed
  // Confidence-aware shelf framing: on a lower-confidence diagnosis, name the
  // match as probable and steer the user to confirm before buying, rather than
  // pushing products with the same certainty as a high-confidence result.
  // undefined keeps the component's default (confident) description.
  const diagnosisShelfDescription = isHighConfidenceDiagnosis
    ? undefined
    : 'These match the most likely diagnosis. Confirm the problem first, then check each product label for your grass and conditions.'
  const herbicideHandoff = primary.type === "weed"
    ? {
        herbicideApplicationType: "post-emergent" as const,
        herbicideTargetWeeds: [primary.name],
      }
    : null

  // Peak-frame CTA label. Long/multi-part problem names ("Broadleaf/buckhorn
  // plantain") would overflow the button, so drop to the generic "treatment"
  // noun past ~22 chars; the full name still leads the H1 above.
  const ctaProblemLabel = primaryProblemName.length > 22 || primaryProblemName.length === 0
    ? "treatment"
    : primaryProblemName

  // Peak CTA → open the digest ask (its root owns #care-plan-upgrade): scroll,
  // pulse, and put the caret in the email field. Scrolling alone left the user
  // to find the ask on a card that also carries two OAuth pills.
  const handleHeroCtaClick = () => {
    trackNextAction('open_treatment_plan')
    trackEvent("diagnosis_hero_cta_clicked", {
      problem: primary.name,
      confidence: result.confidence,
      severity: primary.severity,
      location: "hero_plan_capture",
      cta_location: "hero_plan_capture",
      analysis_id: savedAnalysisId ?? undefined,
      funnel_id: funnelId ?? undefined,
    })
    sendUserToGate(gateEmailInputRef, revealGateEmail)
  }

  // "Why we think it's X" — rendered in two breakpoint-gated slots (mirrors
  // identify's renderWhyThisId): under the photo inside the left column on lg
  // (anchored to the evidence it explains, auto-open since desktop has the
  // vertical room), and as a third grid child on mobile so the order stays
  // photo -> digest -> why (collapsible, closed by default). Gated on
  // overallAssessment — no assessment, no row.
  const renderWhy = result.overallAssessment
    ? (alwaysOpen: boolean) => {
        const open = alwaysOpen || whyExpanded
        return (
          <div className="min-w-0">
            {alwaysOpen ? (
              <div className="w-full rounded-xl border border-green-100 bg-green-50/60 px-4 py-3 text-left">
                <span className="text-[13px] font-semibold text-green-800 leading-snug">
                  Why we think it&apos;s {primaryProblemName}
                </span>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setWhyExpanded((v) => !v)}
                aria-expanded={whyExpanded}
                className="w-full flex items-center justify-between gap-2 rounded-xl border border-green-100 bg-green-50/60 px-4 py-3 text-left transition-colors hover:bg-green-50"
              >
                <span className="text-[13px] font-semibold text-green-800 leading-snug">
                  Why we think it&apos;s {primaryProblemName}
                </span>
                <ChevronDown
                  className={cn(
                    "w-4 h-4 flex-shrink-0 text-green-700 transition-transform",
                    whyExpanded && "rotate-180",
                  )}
                  aria-hidden="true"
                />
              </button>
            )}
            {open && (
              <div className="mt-2 rounded-xl border border-gray-100 bg-white p-3 md:p-4">
                <ConfidenceMeter confidence={result.confidence} />
                <div className="mt-2 space-y-2">
                  <p className="text-xs md:text-[13px] text-gray-600 leading-relaxed">
                    {result.overallAssessment}
                  </p>
                  <p className="text-xs md:text-[13px] text-gray-500 leading-relaxed">
                    Image quality: {result.imageQuality}
                  </p>
                </div>
              </div>
            )}
          </div>
        )
      }
    : null

  return (
    <section id="results-section" className={cn("relative px-3 py-6 md:px-10 md:py-8 scroll-mt-24 overflow-x-hidden", className)}>
      <AnalysisResultFrame>
            {/* Screen-reader announcement of the diagnosis outcome. */}
            <p role="status" aria-live="polite" className="sr-only">
              {`Diagnosis complete. ${result.problems.length} problem${result.problems.length === 1 ? '' : 's'} found. Lead issue: ${primary.name}, ${primary.severity} severity.`}
            </p>

            {refinementChainStrip}

            {/* Verdict-first centered header — mirrors identify's hero. Moved out
                of the left grid column so the page opens on the verdict, not the
                photo: eyebrow → verdict H1 → severity/urgency/type/confidence
                chip row → peak CTA. The problem name is intentionally NOT green-
                highlighted (unlike identify's grass name) — green reads as
                "good", wrong for a weed/disease verdict. Severity carries the
                color instead. */}
            <div className="text-center mb-5 md:mb-8">
              <div className="inline-flex items-center gap-1.5 rounded-full border border-green-100 bg-green-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-green-700 mb-3 md:mb-4">
                <Shield className="w-3 h-3" strokeWidth={2.5} />
                Diagnosis
              </div>

              <h1 className="text-[1.75rem] leading-[1.08] md:text-4xl lg:text-[2.75rem] lg:leading-[1.05] font-extrabold tracking-[-0.02em] text-gray-900">
                {isHighConfidenceDiagnosis
                  ? `${primaryProblemName}, most likely.`
                  : `This looks like ${primaryProblemName}.`}
              </h1>

              {/* Meta chips — the severity/urgency/type that used to fill the big
                  left-column module, now a scannable centered row (recognition
                  over recall). Severity is the focal signal, color-coded via the
                  shared getSeverityStyles tints. */}
              <div className="mt-3 md:mt-4 flex flex-wrap items-center justify-center gap-2">
                <span className={cn("inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ring-1 ring-inset", primarySeverity.bg, primarySeverity.text, primarySeverity.border)}>
                  <span className={cn("h-2 w-2 rounded-full", primarySeverity.dot)} />
                  {capitalize(primary.severity)}
                </span>
                <span className={cn(
                  "inline-flex items-center rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ring-1 ring-inset",
                  primary.urgency === 'immediate'
                    ? "bg-red-50 text-red-600 ring-red-200"
                    : "bg-white text-gray-600 ring-black/[0.08]",
                )}>
                  {getUrgencyLabel(primary.urgency)}
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 px-3 py-1 text-[11px] font-semibold text-gray-600 ring-1 ring-inset ring-black/[0.06]">
                  <span className="flex-shrink-0">{getTypeIcon(primary.type)}</span>
                  {getTypeLabel(primary.type)}
                </span>
                {/* Image quality only when it's a problem — a "Good" chip is
                    noise at the verdict peak; "Fair/Poor" is an actionable
                    caution. */}
                {result.imageQuality !== 'Good' && (
                  <span className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
                    Image: {result.imageQuality}
                  </span>
                )}
              </div>

              <p className="mt-3 mx-auto max-w-2xl text-sm md:text-base text-gray-500 leading-relaxed">
                {result.confidence}% match
                {extraCount > 0 && (
                  <>
                    {' '}&middot; +{extraCount} more {extraCount === 1 ? 'issue' : 'issues'}{' '}
                    {/* "covered in your plan" is a coverage claim, and on a
                        safety hold there is no plan covering anything. The
                        count itself is a finding and stays. */}
                    {safetyValidatorFailedClosed ? 'found' : 'covered in your plan'}
                  </>
                )}
              </p>
              {/* The caveat travels with the number, not only with the footer
                  disclaimer three screens down. */}
              <p className="mt-1 mx-auto max-w-2xl text-xs text-gray-400 leading-relaxed">
                {DIAGNOSIS_SCORE_CAVEAT}
              </p>

              {/* Weed problems with a matching /weeds guide get a durable
                  deep-dive link — the diagnosis result is ephemeral, the guide
                  is the bookmarkable follow-up (and an internal-linking win). */}
              {primary.type === 'weed' && primary.matchedSlug && weedPageDisplayName(primary.matchedSlug) && (
                <p className="mt-2">
                  <Link
                    href={`/weeds/${primary.matchedSlug}`}
                    onClick={() =>
                      trackEvent('diagnosis_weed_guide_link_clicked', {
                        weed_slug: primary.matchedSlug,
                      })
                    }
                    className="inline-flex min-h-[36px] items-center px-2 text-sm font-medium text-green-700 underline underline-offset-2 hover:text-green-800"
                  >
                    Read the full {weedPageDisplayName(primary.matchedSlug)} identification &amp; control guide
                  </Link>
                </p>
              )}

              {/* Same durable deep link for pest findings. */}
              {pestGuideSlug && (
                <p className="mt-2">
                  <Link
                    href={`/pests/${pestGuideSlug}`}
                    onClick={() =>
                      trackEvent('diagnosis_pest_guide_link_clicked', {
                        pest_slug: pestGuideSlug,
                      })
                    }
                    className="inline-flex min-h-[36px] items-center px-2 text-sm font-medium text-green-700 underline underline-offset-2 hover:text-green-800"
                  >
                    Read the full {pestPageDisplayName(pestGuideSlug)} identification &amp; control guide
                  </Link>
                </p>
              )}

              {/* Peak-frame CTA — the verdict viewport finally has an action
                  (Peak-End). Anchors to the digest ask (#care-plan-upgrade),
                  which sits below the fold on mobile. Anon-with-treatments only;
                  authed users already have their plan, and the healthy/failed/
                  monitor paths never reach this branch. */}
              {showHeroPlanCapture && (
                <div className="mt-4 md:mt-5">
                  <Button
                    onClick={handleHeroCtaClick}
                    className="w-full sm:w-auto bg-linear-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white min-h-[48px] px-6 rounded-xl font-semibold text-[15px] shadow-lg hover:shadow-xl transition"
                  >
                    {conversionPlanOnSafetyHold
                      ? 'Save this diagnosis'
                      : `Get my free ${ctaProblemLabel} plan`}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </div>

            {/* 2-column grid: LEFT = the photo evidence, RIGHT = the CTA card.
                min-w-0 on the container + each child is required — grid items
                default to min-width:auto, so a wide descendant (email card, long
                evidence strings) would otherwise blow the grid past viewport
                width and clip the right column. */}
            <div className="grid gap-6 md:gap-8 lg:grid-cols-[1.05fr_1fr] lg:gap-12 mb-6 md:mb-10 items-start min-w-0">
              {/* LEFT — the analyzed photo. Single image (diagnosis has no
                  reference photo yet). Full left-column width on desktop so it
                  reads as the payoff anchor; a short banner on mobile so it's
                  evidence attached to the verdict above, not an orphaned square. */}
              <div className="min-w-0">
                <div className="relative flex-shrink-0 w-full">
                  <div
                    className={cn(
                      "relative w-full h-44 md:h-auto md:aspect-square lg:aspect-[4/3] bg-linear-to-br from-gray-100 to-gray-200 overflow-hidden ring-1 ring-black/[0.04]",
                      CARD_RADIUS,
                      CARD_SHADOW,
                    )}
                    style={{ viewTransitionName: 'grass-photo' }}
                  >
                    {selectedEvidenceImage && (
                      <img
                        src={selectedEvidenceImage}
                        alt={`Your analyzed lawn photo ${selectedEvidenceImageIndex + 1} of ${evidenceImages.length}`}
                        className="w-full h-full object-cover"
                      />
                    )}
                    {/* Numbered evidence pins — projected through the actual
                        object-cover crop (the fixed-height banner crops a
                        typical portrait photo heavily, so raw source-normalized
                        coords would drift onto unrelated grass). Markers with
                        name labels, not navigation: detail cards for problems
                        2+ live behind the plan gate. */}
                    {selectedEvidenceImage && (
                      <DiagnosisPhotoPins
                        problems={result.problems}
                        imageSrc={selectedEvidenceImage}
                        selectedImageIndex={selectedEvidenceImageIndex}
                        imageCount={evidenceImages.length}
                      />
                    )}
                  </div>
                  {evidenceImages.length > 1 && (
                    <div className="mt-3 flex gap-2 overflow-x-auto pb-1" aria-label="Diagnosis evidence photos">
                      {evidenceImages.map((image, index) => (
                        <button
                          key={`${image}-${index}`}
                          type="button"
                          onClick={() => setSelectedEvidenceImageIndex(index)}
                          aria-label={`Show evidence photo ${index + 1}`}
                          aria-pressed={selectedEvidenceImageIndex === index}
                          className={cn(
                            "relative h-14 w-20 flex-none overflow-hidden rounded-lg border-2 bg-gray-100 transition-colors",
                            selectedEvidenceImageIndex === index
                              ? "border-green-600"
                              : "border-transparent hover:border-gray-300",
                          )}
                        >
                          <img src={image} alt="" className="h-full w-full object-cover" />
                          <span className="absolute bottom-1 left-1 rounded bg-black/65 px-1.5 py-0.5 text-[10px] font-bold text-white">
                            {index + 1}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Why-this-ID — desktop slot, anchored under the photo (auto-open,
                    desktop has the room). Mobile uses the collapsed 3rd grid child. */}
                {renderWhy && (
                  <div className="hidden lg:block mt-6 min-w-0">{renderWhy(true)}</div>
                )}
              </div>

              {/* RIGHT — CTA card. Anonymous: gated plan/upgrade card with the
                  blurred lock-teaser + email/OAuth capture. Authenticated:
                  a treatment-summary plan card that anchors to the full plan. */}
              <div className="flex flex-col gap-4 min-w-0 justify-center">
                {isAnonymous && canShowConversionPlan ? (
                  <DiagnosisResultDigest
                    safetyHold={conversionPlanOnSafetyHold}
                    result={displayResult}
                    primaryProblemName={primaryProblemName}
                    savedAnalysisId={savedAnalysisId}
                    entryPath={entryPath}
                    carePlanEmail={carePlanEmail}
                    emailSuggestion={emailSuggestion}
                    isSubmittingCarePlan={isSubmittingCarePlan}
                    carePlanEmailError={carePlanEmailError}
                    emailGateCompleted={emailGateCompleted}
                    timelineRedirectUrl={timelineRedirectUrl}
                    redirectUrl={redirectUrl}
                    onBeforeOAuth={onBeforeOAuth}
                    onEmailChange={onEmailChange}
                    onEmailKeyPress={onEmailKeyPress}
                    onEmailSubmit={onEmailSubmit}
                    onPrimaryCtaClick={onPrimaryCtaClick}
                    onAcceptEmailSuggestion={onAcceptEmailSuggestion}
                    onDismissEmailSuggestion={onDismissEmailSuggestion}
                    onSeeCarePlan={onSeeCarePlan}
                    zipCode={zipCode}
                    hasEmailCapture={hasEmailCapture}
                    emailInputRef={gateEmailInputRef}
                    emailRevealed={gateEmailRevealed}
                    onEmailRevealed={() => setGateEmailRevealed(true)}
                    onGateEngaged={() => setGateEngaged(true)}
                  />
                ) : isAnonymous ? (
                  <ActionableFindingCard
                    safetyHold={safetyValidatorFailedClosed}
                    primaryProblemName={primaryProblemName}
                    immediateAction={result.immediateActions[0]}
                    zipCode={zipCode}
                    redirectUrl={redirectUrl}
                    onBeforeOAuth={onBeforeOAuth}
                  />
                ) : (
                  <AuthenticatedPlanCard
                    safetyHold={safetyValidatorFailedClosed}
                    treatmentCount={treatmentRecommendations.length}
                    primaryTreatment={treatmentRecommendations[0]}
                    firstAction={result.immediateActions.find((action) => action.trim().length > 0)}
                    analysisId={savedAnalysisId}
                    outcome={result.outcome || "problems"}
                    entryPath={entryPath}
                  />
                )}
              </div>

              {/* Why-this-ID — mobile/tablet slot (3rd grid child) so the order
                  stays photo -> digest -> why (collapsed, closed). On lg it
                  renders inside the left column under the photo instead. */}
              {renderWhy && <div className="min-w-0 lg:hidden">{renderWhy(false)}</div>}
            </div>

            {/* FULL-WIDTH sections below the grid, in identify's order. */}
            <div className="space-y-6 md:space-y-8 mt-6 md:mt-8">
              {/* Immediate Actions (if severe) — never gated, safety info.
                  Relocated here from the left verdict column so the hero grid
                  stays balanced; it remains the first thing after the hero, now
                  full-width. Numbered checklist; red is an accent on the header
                  + badges, not a wall of red text (alarm-fatigue). Width-capped
                  for readable line length while sitting in the full-width rhythm. */}
              {/* Honest account of the two sections the server emptied. It
                  leads the full-width stack because it explains an absence the
                  reader is about to notice. */}
              {safetyValidatorFailedClosed && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4 md:p-5">
                  <div className="flex items-start gap-2.5">
                    <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700" aria-hidden="true" />
                    <div className="min-w-0">
                      <h3 className="text-sm font-bold leading-snug text-gray-900">
                        We are holding back the treatment steps
                      </h3>
                      <p className="mt-1 text-[13px] leading-relaxed text-gray-700">
                        Our product safety check did not finish on this diagnosis, so we are not
                        listing products or immediate steps for it. The findings above are unchanged.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {hasImmediateActions && hasSevereProblems && (
                <ImmediateActionsBlock actions={result.immediateActions} severe />
              )}

              {/* In-flow save ask, right where the free value stops. Same
                  conditions as the gate (anon, treatable diagnosis, pre-capture)
                  plus a self-suppress once the gate itself has been engaged, so
                  the page never shows two live asks for the same thing. The
                  strip hides itself at lg+, where the gate is an on-screen
                  sidebar rather than something already scrolled past. */}
              {/* Suppressed on a safety hold: this strip's whole pitch is
                  "the full plan adds every treatment step, product picks, and
                  prevention", and on a fail-closed result there is no full
                  plan behind the ask for anyone. */}
              {isAnonymous &&
                canShowConversionPlan &&
                !conversionPlanOnSafetyHold &&
                hasEmailCapture &&
                !emailGateCompleted &&
                !gateEngaged && (
                <DiagnosisActionsSaveStrip
                  problemName={primaryProblemName}
                  savedPlanContext={diagnosisSavedPlanContext({
                    result,
                    savedAnalysisId,
                    entryPath,
                    primaryProblemName,
                  })}
                  redirectUrl={timelineRedirectUrl ?? redirectUrl}
                  onBeforeOAuth={onBeforeOAuth}
                  onEmailIt={() => sendUserToGate(gateEmailInputRef, revealGateEmail)}
                />
              )}

              {/* Pro referral: the DIY checklist above, or hire it out.
                  Outbound Angi affiliate link (2026-07-18 pivot). Gated to
                  severe problems, plus moderate ones flagged as immediate
                  (widened 2026-07-18 after launch). */}
              {primary &&
                (hasSevereProblems ||
                  (primary.severity === 'moderate' && primary.urgency === 'immediate')) && (
                <DiagnosisProReferralCard problem={primary} entryPath={entryPath} />
              )}

              {/* Treatment-plan accordions + prevention tips. Unlocked for
                  authed users, anon users with no treatments, OR anon users who
                  gave their email (emailGateCompleted) — email now reveals the
                  full plan in place, matching identify. */}
              {(!isAnonymous || emailGateCompleted || !hasTreatments || !canShowConversionPlan) && (
                <div id="diagnosis-plan" className="scroll-mt-24 space-y-6">
                  {canShowTreatmentGuidance && (
                    <div id="treatment-plan" className="scroll-mt-24">
                      {/* No section H2 here — the hero's "Your treatment plan"
                          heading already labels this, and each accordion is
                          self-titled by its problem name. The first treatment
                          auto-expands (its best-window chip is suppressed since
                          the hero's step-1 card already shows it). */}
                      <div className="space-y-2">
                        {treatmentRecommendations.map((rec, i) => (
                          <TreatmentSection
                            key={i}
                            recommendation={rec}
                            defaultExpanded={i === 0}
                            hideBestWindow={i === 0}
                            showProducts={canUseVerifiedTreatment}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Non-severe Immediate Actions */}
                  {hasImmediateActions && !hasSevereProblems && (
                    <ImmediateActionsBlock actions={result.immediateActions} severe={false} />
                  )}

                  {/* Prevention Tips — progressive disclosure, collapsed by
                      default. This is the authenticated / no-treatment path ONLY;
                      the diagnosis-gate-v1 `partial` arm and the anonymous blur
                      teaser render their own prevention blocks inside
                      AnonymousPlanGate and are intentionally left untouched so
                      the experiment's gated surface is unchanged. */}
                  {result.preventionTips.length > 0 && (
                    <CollapsibleSection
                      title="Prevention tips"
                      icon={Sprout}
                      defaultExpanded={false}
                      itemCount={result.preventionTips.length}
                      storageKey="diagnosis-prevention-tips"
                    >
                      <ul className="space-y-2 pt-1">
                        {result.preventionTips.map((tip, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-sm text-green-700">
                            <Sprout className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </CollapsibleSection>
                  )}
                </div>
              )}

              {/* Next photo. A disagreement with the earlier scan takes the
                  slot when there is one; otherwise another view of the same
                  damage leads and the full restart stays underneath. The pair
                  follows the plan and precedes the product shelf, the tool grid,
                  the cross-sell and the app card, matching the healthy branch
                  and the identify result: it acts on the diagnosis the user just
                  read, and behind a viewport of commerce it was scanned past. */}
              <div className="mx-auto w-full max-w-md space-y-2 text-center" ref={refinementOfferRef}>
                {showsConflictCta && (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={refinementPending}
                    onClick={() => {
                      trackNextAction('break_the_tie')
                      startRefinement(refinementReason)
                    }}
                    className="min-h-[44px] w-full rounded-full border-gray-300 font-semibold text-gray-700 hover:border-green-600 hover:bg-white hover:text-green-700"
                  >
                    {refinementLocked ? (
                      <Lock className="mr-2 h-4 w-4" aria-hidden="true" />
                    ) : (
                      <Camera className="mr-2 h-4 w-4" aria-hidden="true" />
                    )}
                    {refinementCtaLabel}{refinementLocked && <span className="sr-only">, free account</span>}
                  </Button>
                )}
                {canRetryAdditive && (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={refinementPending}
                    onClick={() => {
                      trackNextAction('add_supporting_photo')
                      handleRetryAdditive()
                    }}
                    className="min-h-[44px] w-full rounded-full border-gray-300 font-semibold text-gray-700 hover:border-green-600 hover:bg-white hover:text-green-700"
                  >
                    {refinementLocked ? (
                      <Lock className="mr-2 h-4 w-4" aria-hidden="true" />
                    ) : (
                      <Camera className="mr-2 h-4 w-4" aria-hidden="true" />
                    )}
                    {retryAdditiveLabel}{refinementLocked && <span className="sr-only">, free account</span>}
                  </Button>
                )}
                {/* The free-re-run promise requires a live policy: saved pages have no
                    entitlement knowledge, and promising free on a spent root would be
                    false. refinementsRemaining === null means unlimited (premium). */}
                {canRetryAdditive && !refinementLocked && refinementPolicy != null && refinementPolicy.refinementsRemaining !== 0 && (
                  <p className="text-xs text-gray-500">This re-run is free. It will not use another scan.</p>
                )}
                {!hideReplacementRetry && (
                  <button
                    type="button"
                    onClick={(event) => {
                      trackNextAction('retake_photo')
                      onReset(event)
                    }}
                    className="inline-flex min-h-[44px] items-center justify-center px-3 text-sm text-gray-500 transition-colors hover:text-gray-700"
                  >
                    Try a different photo
                  </button>
                )}
              </div>

              {/* Product upsell — curated, condition-matched three-card shelf.
                  getCompleteConditionPicks always resolves exactly three picks
                  for an actionable diagnosis (in-season condition, then
                  companions, then universal backfill), so this is the single
                  shelf path — there is no partial 1-2 card fallback. */}
              {canShowCuratedProductShelf && curatedConditionPicks && (
                <DiagnosisProductPicks
                  problem={primary}
                  conditionSlug={curatedConditionPicks.conditionSlug}
                  conditionType={curatedConditionPicks.conditionType}
                  picks={curatedConditionPicks.picks}
                  hostGrassVerified={result.hostGrassVerified === true}
                  description={diagnosisShelfDescription}
                />
              )}

              {/* Condition deep-link. Placement is a constraint, not a
                  preference: it sits after every capture ask on this screen
                  (hero CTA, gate digest, save strip) so a durable content link
                  can never intercept a user before the personalized offer. It
                  is a quiet text link for the same reason, and it leads the
                  tool grid because both answer "what do I do next". */}
              {conditionNextStep && (
                <ConditionNextStepLink nextStep={conditionNextStep} problemType={primary.type} />
              )}

              {/* Tool deep-links — pre-filled from the ZIP (diagnosis has no
                  grass type, so grassType is empty and those params are
                  omitted by the URL builders). Rendered inline (not collapsed)
                  so the 2×2 tool grid is always visible. Lives entirely outside
                  the gated surface. */}
              {/* The hold strips the verified herbicide params but the tile
                  itself was still an "when to apply a pre- or post-emergent"
                  affordance on the one result where the safety check did not
                  finish. The other three are cultural calculators and stay. */}
              <ResultToolDeeplinks
                grassType={DIAGNOSIS_GRASS_TYPE}
                zip={zipCode}
                suppressHerbicideTiming={safetyValidatorFailedClosed}
                herbicideApplicationType={canUseVerifiedTreatmentSafely ? herbicideHandoff?.herbicideApplicationType : undefined}
                herbicideTargetWeeds={canUseVerifiedTreatmentSafely ? herbicideHandoff?.herbicideTargetWeeds : undefined}
                onNextAction={() => trackNextAction('open_tool')}
              />

              {/* Identify cross-sell (diagnosis → identify). */}
              <IdentifyCrossSell
                uploadedImage={uploadedImage}
                onSwitchToIdentify={handleSwitchToIdentify}
                switchToIdentifyHref={switchToIdentifyHref}
              />

              {/* App download — parity with the identify result (2026-07-12).
                  The identify result carried an AppDownloadCTA; the diagnose result
                  had none. Placed in the bottom next-steps zone, AFTER the email/OAuth
                  peak ask (hero DiagnosisResultDigest) and the identify cross-sell, so
                  it complements the capture triad instead of competing with it. Copy is
                  specific to the diagnose moment (tracking a fix). Self-nulls unless the
                  app-promo env gate is on. */}
              {/* Paid continuation of a diagnosed problem. Placed after the
                  product shelf, the condition link and the tools so all the
                  free value lands first, and far below the hero digest ask,
                  which stays the top of the ladder. A quiet text link in the
                  same weight as the condition deep-link above it, so the screen
                  keeps one primary action. */}
              {showPremiumRecheckContinuation && (
                <div className="text-center">
                  <Link
                    href={premiumContinuationHref}
                    data-testid="diagnosis-premium-recheck-link"
                    onClick={() => trackNextAction('premium_recheck')}
                    className="inline-flex min-h-[44px] items-center gap-1 px-2 text-[13px] font-semibold text-green-700 underline underline-offset-[3px] transition-colors hover:text-green-800"
                  >
                    <Search className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                    Recheck this fix as often as you need with Premium
                  </Link>
                </div>
              )}

              <AppDownloadCTA
                placement="diagnose_result"
                variant="card"
                intent="diagnose_lawn_problem"
                headline="Diagnose a lawn problem from your phone"
                subhead="Use the free iPhone app to scan the affected area, track this fix, and get reminders for the next step."
              />

              {/* Confidence feedback — moved to the bottom so it reads as a
                  wrap-up "did we get this right?" check after the full result. */}
              <ResultConfidenceFeedback
                grassType={primary.name}
                confidence={result.confidence}
                question="Does this look like your problem?"
                positiveText="Great. Glad the diagnosis matches what you're seeing."
                negativeText="Thanks for the feedback. For a more accurate read, try a closer, well-lit photo of the affected area."
                eventName="diagnosis_confidence_feedback"
                verifyLabel={false}
                eventProperties={{
                  analysis_id: savedAnalysisId ?? undefined,
                  entry_path: entryPath,
                  model: result.model,
                  pipeline_version: result.pipelineVersion,
                  host_grass_verified: result.hostGrassVerified === true,
                }}
                onResponse={(positive) => {
                  if (!positive) setReviewRequestedByFeedback(true)
                }}
              />
              {showReviewRequestOffer && (
                <DiagnosisReviewRequest
                  result={result}
                  analysisId={savedAnalysisId}
                  user={user}
                  analyticsReady={!authLoading}
                  reason={reviewRequestedByFeedback ? "disputed" : "high_risk"}
                  defaultOpen={reviewRequestedByFeedback}
                  funnelProperties={diagnosisFunnelProperties}
                  onNextAction={() => trackNextAction('request_second_look')}
                />
              )}
            </div>
      </AnalysisResultFrame>

      {/* Mobile sticky save bar — covers the dead zone between the hero and the
          digest ask for anon users (the identify funnel showed 70% never
          scrolled the ask into view). Anon-with-treatments only; the bar
          self-hides near the top and once the digest is on screen. */}
      {/* Suppressed on a safety hold: the bar's copy is "Your <problem> plan
          is ready / Save your treatment plan free", which is the exact claim
          the safety notice retracts. */}
      {isAnonymous && canShowConversionPlan && !conversionPlanOnSafetyHold && !emailGateCompleted && (
        <ResultStickySaveBar
          itemName={ctaProblemLabel}
          kind="problem"
          entryPath={entryPath}
        />
      )}
    </section>
  )
}

// Defensive capture for a result that is actionable but whose deterministic
// treatment enrichment could not be loaded. It still gives the user a next
// step and a save path instead of false reassurance.
function ActionableFindingCard({
  primaryProblemName,
  immediateAction,
  zipCode,
  redirectUrl,
  onBeforeOAuth,
  safetyHold = false,
}: {
  primaryProblemName: string
  immediateAction?: string
  zipCode?: string
  redirectUrl?: string
  onBeforeOAuth?: () => void
  /** Reachable on a fail-closed result whose treatments AND deterministic
   *  fallback both come back empty. The route empties `immediateActions`, so
   *  the card falls through to its own "use a treatment whose label lists..."
   *  line, which is a product instruction on the screen that says we are not
   *  listing products. */
  safetyHold?: boolean
}) {
  const impressionRef = useImpressionEvent<HTMLDivElement>(
    "diagnosis_actionable_unmatched",
    { problem: primaryProblemName || undefined },
  )

  const handleOAuth = () => {
    stashEmailCaptureAttribution("diagnosis_actionable_unmatched")
    onBeforeOAuth?.()
  }

  return (
    <div ref={impressionRef} className={cn("bg-white p-5 sm:p-6 md:p-8", PREMIUM_CARD)}>
      <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-amber-700 mb-1.5">
        <AlertTriangle className="w-3 h-3" strokeWidth={2.5} />
        {safetyHold ? 'What we found' : 'Start with this'}
      </div>
      <p className="text-lg md:text-xl font-extrabold tracking-tight text-gray-900 leading-tight">
        This finding needs attention.
      </p>
      <p className="mt-2 text-sm leading-relaxed text-gray-600">
        {safetyHold
          ? 'Our product safety check did not finish, so we are not naming a treatment for this one. Keep an eye on the area and compare it with this photo in a week.'
          : immediateAction || `Use a treatment whose label lists ${primaryProblemName || 'this problem'} and your lawn conditions.`}
      </p>
      <ResultEmailCaptureCard
        source="diagnosis_actionable_unmatched"
        toolDisplayName="diagnosis and next-step checklist"
        headline="Save this diagnosis and next step"
        subcopy="Keep the result and get a reminder to check the affected area again."
        successSubcopy="Your diagnosis and re-check reminder are on the way."
        ctaLabel="Email my next step"
        zipCode={zipCode || null}
        className="mt-5"
        accountLead={
          redirectUrl
            ? {
                redirectTo: redirectUrl,
                headline: "Save this diagnosis to a free account",
                subcopy: "Keep this result and its next step in one place.",
                dividerLabel: "or get it by email",
                onBeforeOAuth: handleOAuth,
              }
            : undefined
        }
      />
    </div>
  )
}

// Authenticated right-column plan card: leads with the first treatment and
// anchors down to the full plan below.
function AuthenticatedPlanCard({
  treatmentCount,
  primaryTreatment,
  firstAction,
  analysisId,
  outcome,
  entryPath,
  safetyHold = false,
}: {
  treatmentCount: number
  primaryTreatment: TreatmentRecommendation | undefined
  firstAction?: string
  analysisId?: string | null
  outcome: string
  entryPath: string
  /** The chemical-safety validator failed closed. The notice above this card
   *  says we are holding the treatment steps back, so the card cannot say a
   *  treatment plan was saved, sample a step out of `culturalPractices` (the
   *  route empties `products`, not that prose, and it names chemicals), name a
   *  treatment window, or send the reader to accordions that do not render. */
  safetyHold?: boolean
}) {
  const treatmentStep = firstAction?.trim() || firstTreatmentStep(primaryTreatment)
  const isMonitoringPlan = safetyHold || treatmentCount === 0 || !primaryTreatment

  return (
    <SavedPlanActionCard
      eyebrow={safetyHold
        ? "Diagnosis saved"
        : isMonitoringPlan ? "Monitoring plan saved" : "Treatment plan saved"}
      title={safetyHold
        ? "Your diagnosis is saved"
        : isMonitoringPlan
          ? "Your monitoring plan is saved"
          : treatmentCount === 1
            ? "Your treatment plan is saved"
            : `Your ${treatmentCount}-part treatment plan is saved`}
      firstAction={safetyHold
        ? "Check the affected area again in 7 days and compare it with this photo."
        : treatmentStep || "Check the affected area again in 7 days and compare it with this photo."}
      actionMeta={safetyHold || !primaryTreatment
        ? "Your result and prevention guidance are available from your dashboard."
        : `Best window: ${primaryTreatment.bestTreatmentWindow}`}
      analyticsContext={{
        analysisId: analysisId || undefined,
        mode: "diagnose",
        planType: isMonitoringPlan ? "monitoring" : "treatment",
        outcome,
        entryPath,
        subject: primaryTreatment?.problemName || "Healthy lawn",
      }}
      planDestination={entryPath === "saved_result" ? "saved_diagnosis_result" : "diagnosis_result"}
      primaryLabel={isMonitoringPlan ? "Review prevention steps" : "Open treatment steps"}
      primaryDestination="diagnosis_plan"
      onPrimaryAction={() => {
        scrollToDiagnosisPlan()
      }}
    />
  )
}

// Consolidated anonymous ask — the diagnose equivalent of ResultCarePlanDigest.
// ONE card: lead treatment (concrete value) → blurred "+N more" teaser →
// impermanence cue → a single email-first + OAuth ask (ResultUpgradeCard
// embedded, personalized to the diagnosed problem) → trust line. Replaces the
// old split of (hero OAuth gate + separate below-grid email card) and the
// duplicate blurred-plan clone. Email capture unlocks the real treatment plan
// in place (the gate below reads emailGateCompleted).
function DiagnosisResultDigest({
  result,
  primaryProblemName,
  savedAnalysisId,
  entryPath,
  carePlanEmail,
  emailSuggestion,
  isSubmittingCarePlan,
  carePlanEmailError,
  emailGateCompleted,
  timelineRedirectUrl,
  redirectUrl,
  onBeforeOAuth,
  onEmailChange,
  onEmailKeyPress,
  onEmailSubmit,
  onPrimaryCtaClick,
  onAcceptEmailSuggestion,
  onDismissEmailSuggestion,
  onSeeCarePlan,
  zipCode,
  hasEmailCapture,
  emailInputRef,
  emailRevealed,
  onEmailRevealed,
  onGateEngaged,
  safetyHold = false,
}: {
  result: DiagnosisResult
  primaryProblemName: string
  savedAnalysisId?: string | null
  entryPath: string
  carePlanEmail?: string
  emailSuggestion?: EmailSuggestion | null
  isSubmittingCarePlan?: boolean
  carePlanEmailError?: string | null
  emailGateCompleted?: boolean
  timelineRedirectUrl?: string
  redirectUrl?: string
  onBeforeOAuth?: () => void
  onEmailChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onEmailKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>, location?: string) => void
  /** `location` is sent on care_plan_cta_clicked. */
  onEmailSubmit?: (location?: string) => void
  onPrimaryCtaClick?: (method: "oauth" | "email") => void
  onAcceptEmailSuggestion?: () => void
  onDismissEmailSuggestion?: () => void
  onSeeCarePlan?: () => void
  zipCode?: string
  hasEmailCapture: boolean
  /** Lets the hero CTA and the actions strip land the caret in this card's
   *  email field. */
  emailInputRef?: React.RefObject<HTMLInputElement | null>
  /** Opens the collapsed email lane, so the same two CTAs have a field to focus. */
  emailRevealed?: boolean
  /** The card's own reveal link fired — keep `emailRevealed` in sync. */
  onEmailRevealed?: () => void
  /** Called the moment the user engages this gate (email focus, email submit,
   *  or OAuth), so the surface can retire the competing asks. */
  onGateEngaged?: () => void
  /** The chemical-safety validator failed closed, so the screen is telling the
   *  reader we are holding the treatment back. The ask stays (the finding is
   *  real and worth saving) but it must not name, tease, or sample a plan. */
  safetyHold?: boolean
}) {
  const lead = result.treatmentRecommendations[0]
  const rest = result.treatmentRecommendations.slice(1)
  const problemLabel = primaryProblemName || "your lawn problem"
  const savedPlanContext = diagnosisSavedPlanContext({
    result,
    savedAnalysisId,
    entryPath,
    primaryProblemName,
  })
  const impressionRef = useImpressionEvent<HTMLDivElement>(
    "diagnosis_gate",
    savedPlanEventProperties(savedPlanContext, { problem: primaryProblemName || undefined }),
    { enabled: !emailGateCompleted },
  )
  const handleGateOAuth = () => {
    stashEmailCaptureAttribution("diagnosis_gate")
    onGateEngaged?.()
    onBeforeOAuth?.()
  }

  // Post-capture: the treatment plan unlocks in place below, so here we just
  // celebrate + point at it. Mirrors identify's CarePlanSentSuccess swap.
  if (emailGateCompleted) {
    return (
      <div className={cn("bg-white p-4 md:p-7", CARD_RADIUS, CARD_BORDER, CARD_SHADOW)}>
        <CarePlanSentSuccess
          region={result.region}
          zipPrefix={zipCode ? zipCode.slice(0, 3) : undefined}
          email={carePlanEmail || undefined}
          onSeePlan={() => {
            // Diagnosis unlocks in place — scroll to the now-revealed plan below,
            // NOT to /timeline (which has no diagnosis data).
            if (typeof document !== "undefined") {
              document
                .getElementById("treatment-plan")
                ?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
          }}
        />
      </div>
    )
  }

  // Value-stack checklist copy, built from the real diagnosis data. Mirrors
  // identify's ResultCarePlanDigest "Your free plan is ready" list — value
  // BEFORE the ask, which the old email-first gate skipped entirely.
  const issueCount = result.problems.length
  const treatmentCount = result.treatmentRecommendations.length
  // On a safety hold the treatment lines would promise the exact thing the
  // notice above retracts, so both swap to what an account still delivers:
  // the finding itself, and the seasonal timing that is not chemical advice.
  const stepLine = safetyHold
    ? `Your ${problemLabel} finding, kept with the photo`
    : treatmentCount <= 1
      ? `Step-by-step ${problemLabel} treatment, starting below`
      : `Treatment steps for all ${issueCount} issue${issueCount === 1 ? "" : "s"} found`
  const regionLine = safetyHold
    ? (zipCode
        ? `Seasonal lawn care timing for ZIP ${zipCode}`
        : "Seasonal lawn care timing for your region")
    : zipCode
      ? `Best treatment windows for ZIP ${zipCode}`
      : "Best treatment windows for your region"

  // Blurred lock tease. Built from what's ACTUALLY gated — the lead treatment's
  // products plus any additional treatments — so even a single-treatment result
  // (the common case) shows a real tease instead of empty space. Recognition
  // over recall (Priming): named cards, blurred, not a wall of text.
  const teaseItems: string[] = [
    ...rest.map((r) => r.problemName),
    ...(lead?.products?.map((p) => p.name) ?? []),
  ]
  const uniqueTease = Array.from(new Set(teaseItems.filter(Boolean))).slice(0, 3)
  const lockLine =
    rest.length > 0
      ? `Full plan: ${treatmentCount} treatments, products & prevention`
      : "Full plan: every treatment step, products & prevention"

  // One free step from the lead treatment, shown in the "Start here" card so the
  // user sees the plan has real, actionable depth (not just a name + a gate).
  const leadStep = firstTreatmentStep(lead)

  return (
    <div
      // The digest IS the anonymous ask, so it owns #care-plan-upgrade — the
      // scroll target for the hero peak CTA and the mobile sticky save bar
      // (matches identify's ResultCarePlanDigest). The unlocked treatment
      // accordions below own #treatment-plan.
      id="care-plan-upgrade"
      ref={impressionRef}
      className={cn(
        // Green→white→amber wash behind the glass card so the frosting reads
        // (identical to the identify digest for cross-surface consistency).
        "relative w-full max-w-full min-w-0 overflow-hidden rounded-2xl border border-gray-200 bg-linear-to-b from-green-50/70 via-white to-amber-50/30 p-4 md:p-6 shadow-sm scroll-mt-24",
      )}
    >
      {/* Glass account card — value checklist directly above the ask (1a:
          account-first, email a quiet fallback). No backdrop-blur (it would
          only blur the flat parent wash while forcing a mobile compositing
          layer); the gradient + inset highlight + green shadow carry the look. */}
      <div className="relative rounded-[20px] border border-white/70 bg-linear-to-b from-white/80 to-white/60 p-5 [box-shadow:0_12px_40px_rgba(22,163,74,0.10),inset_0_1px_0_rgba(255,255,255,0.85)]">
        <p className="mb-3 text-base font-bold tracking-tight text-green-900">
          {safetyHold ? "Save this diagnosis" : "Your treatment plan is ready"}
        </p>
        <ul className="mb-4 space-y-2">
          <li className="flex items-start gap-2">
            <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" strokeWidth={2.25} />
            <span className="text-sm leading-snug text-gray-700">{stepLine}</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" strokeWidth={2.25} />
            <span className="text-sm leading-snug text-gray-700">{regionLine}</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" strokeWidth={2.25} />
            <span className="text-sm leading-snug text-gray-700">
              A re-check reminder timed to your season
            </span>
          </li>
        </ul>

        {/* The ask — account-first pill OAuth ("Save my plan with Google/Apple")
            with the email lane behind a quiet "get it by email" link, matching
            the identify result card. askOrder defaults to account_first so
            savePlanOauth's pill treatment renders (email_first would suppress
            it). emailRevealed lets this surface's own email CTAs open the lane
            (see sendUserToGate) rather than leaving them to focus a field that
            isn't mounted. */}
        {/* The email lane's contract on this surface is "give us your address
            and the full treatment plan unlocks in place" (emailGateCompleted
            reveals the accordions). On a safety hold `canShowTreatmentGuidance`
            is false, so that unlock delivers nothing — the honest ask that
            remains is the account, which really does keep the finding. Same
            shape identify already ships on its Unknown/Other branch. */}
        {hasEmailCapture && !safetyHold ? (
          <ResultUpgradeCard
            embedded
            savePlanOauth
            emailRevealed={emailRevealed}
            onEmailRevealed={onEmailRevealed}
            ctaVariant="specific"
            problemName={primaryProblemName}
            email={carePlanEmail ?? ""}
            emailSuggestion={emailSuggestion ?? null}
            isSubmitting={!!isSubmittingCarePlan}
            source={GATE_SOURCE}
            emailError={carePlanEmailError}
            timelineRedirectUrl={timelineRedirectUrl ?? redirectUrl}
            savedPlanContext={savedPlanContext}
            emailInputRef={emailInputRef}
            onEmailFocus={onGateEngaged}
            onPrimaryCtaClick={(method) => {
              onGateEngaged?.()
              onPrimaryCtaClick?.(method)
            }}
            onBeforeOAuth={handleGateOAuth}
            onEmailChange={onEmailChange!}
            onEmailKeyPress={(e) => onEmailKeyPress!(e, DIGEST_CARE_PLAN_LOCATION)}
            onSubmit={() => onEmailSubmit!(DIGEST_CARE_PLAN_LOCATION)}
            onAcceptSuggestion={onAcceptEmailSuggestion ?? (() => {})}
            onDismissSuggestion={onDismissEmailSuggestion ?? (() => {})}
          />
        ) : (
          // Host didn't thread email handlers, or a safety hold retired the
          // email lane — OAuth-only.
          <>
            <SocialLoginButtons
              placement="diagnosis_result_digest"
              redirectTo={redirectUrl}
              savedPlanContext={savedPlanContext}
              pill
              appleSolid
              googleLabel={safetyHold ? "Save this diagnosis with Google" : "Save my plan with Google"}
              appleLabel={safetyHold ? "Save this diagnosis with Apple" : "Save my plan with Apple"}
              onBeforeRedirect={handleGateOAuth}
            />
            <p className="mt-3 text-xs text-gray-400 text-center">Free · No credit card · No spam</p>
          </>
        )}

        <p className="mt-3 text-center text-xs leading-relaxed text-gray-500">Free, no card.</p>
      </div>

      {/* This month's lead treatment stays free — "Start here". Shows one real
          step (when the model gave us one) so the plan reads as substantive, not
          a bare name gating an empty box. */}
      {lead && !safetyHold && (
        <div className="mt-4 rounded-xl border border-green-200 bg-green-50/60 px-4 py-3">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-green-700">
            Start here
          </div>
          <div className="mt-0.5 text-[15px] font-bold leading-snug text-gray-900">
            {lead.problemName}
          </div>
          <p className="mt-1 text-xs text-gray-500 inline-flex items-center gap-1">
            <CalendarDays className="h-3 w-3 flex-shrink-0" />
            Best window: {lead.bestTreatmentWindow}
          </p>
          {leadStep && (
            <div className="mt-2.5 flex items-start gap-2 border-t border-green-200/70 pt-2.5">
              <Sprout className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" strokeWidth={2.25} />
              <p className="text-[13px] leading-snug text-gray-700">
                <span className="font-semibold text-gray-900">First step:</span> {leadStep}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Lock line names exactly what's gated, then the blurred tease. Both go
          away on a safety hold: nothing is gated behind the ask there, the plan
          is withheld from everyone, so a lock over it would be theatre. */}
      {!safetyHold && (
      <div className="mt-4 flex items-center gap-2">
        <Lock className="h-3.5 w-3.5 text-green-700" strokeWidth={2.25} />
        <span className="text-[13px] font-bold text-green-700">
          {lockLine}
        </span>
      </div>
      )}
      {!safetyHold && uniqueTease.length > 0 && (
        <div
          className="mt-2 flex gap-2 overflow-hidden blur-[2px] opacity-60 pointer-events-none select-none"
          aria-hidden="true"
        >
          {uniqueTease.map((label, i) => (
            <div
              key={i}
              className="min-w-0 flex-1 basis-0 rounded-lg border border-gray-100 bg-white px-2.5 py-2"
            >
              <p className="text-[11px] font-bold text-gray-700 truncate">{label}</p>
              <p className="text-[10.5px] text-gray-500 leading-snug">Step + products</p>
            </div>
          ))}
        </div>
      )}
      {/* Save-to-keep instruction folded into the honest impermanence closer
          (loss-aversion as a closer, not the opener it used to be) — one line
          instead of two restating the same save-or-lose point. */}
      <p className="mt-3 text-center text-[12px] text-gray-500 leading-snug">
        Save above to keep it, or it&apos;s gone when you close this tab.
      </p>
    </div>
  )
}


/**
 * Evidence-pin overlay for the diagnosis photo. Fills the relative photo
 * container (inset-0), measures ITSELF for the container size (its rect ==
 * the container's) and loads the image's natural dimensions, then projects
 * each problem's source-normalized box center through the object-cover crop
 * (lib/diagnosis-pins.ts). Renders nothing until both measurements exist —
 * guessing before load is how pins end up on unrelated grass.
 */
function DiagnosisPhotoPins({
  problems,
  imageSrc,
  selectedImageIndex,
  imageCount,
}: {
  problems: DiagnosisIdentification[]
  imageSrc: string
  selectedImageIndex: number
  imageCount: number
}) {
  const overlayRef = useRef<HTMLDivElement | null>(null)
  const [containerSize, setContainerSize] = useState<{ width: number; height: number } | null>(null)
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null)

  useEffect(() => {
    const node = overlayRef.current
    if (!node) return
    const measure = () => {
      const rect = node.getBoundingClientRect()
      if (rect.width > 0 && rect.height > 0) {
        setContainerSize({ width: rect.width, height: rect.height })
      }
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    let cancelled = false
    // The sibling <img> uses the same src, so this hits the browser cache.
    const img = new Image()
    img.onload = () => {
      if (!cancelled && img.naturalWidth > 0 && img.naturalHeight > 0) {
        setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight })
      }
    }
    img.src = imageSrc
    return () => {
      cancelled = true
    }
  }, [imageSrc])

  if (!containerSize || !naturalSize) {
    return <div ref={overlayRef} className="absolute inset-0" aria-hidden="true" />
  }

  return (
    <div ref={overlayRef} className="absolute inset-0">
      {problems.slice(0, MAX_PINS).map((problem, index) => {
        const sourceIndex = problem.sourceImageIndex
        const belongsToSelectedImage = Number.isInteger(sourceIndex)
          ? sourceIndex === selectedImageIndex
          : imageCount === 1 && selectedImageIndex === 0
        if (!belongsToSelectedImage) return null
        const pin = projectPinThroughCover(problem.boundingBox, index, naturalSize, containerSize)
        if (!pin.visible) return null
        return (
          <span
            key={`${problem.name}-${index}`}
            title={problem.name}
            aria-label={`Problem ${index + 1}: ${problem.name}`}
            className={cn(
              "absolute z-10 inline-flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-[13px] font-bold text-white shadow-[0_2px_8px_rgba(0,0,0,0.35)] ring-2 ring-white",
              index === 0 ? "bg-red-500" : "bg-gray-900/80",
            )}
            style={{ left: `${pin.leftPct}%`, top: `${pin.topPct}%` }}
          >
            {index + 1}
          </span>
        )
      })}
    </div>
  )
}
